import type { WithdrawalContext, WithdrawalResult, WithdrawalSubmitInput } from '@common-models/withdrawal';
import type { PausedSeriesEntry, ResumePausedSeriesResult } from "@bt-common/schemas/bt/series-pause";
import type { NumbersView } from "../../../pages/engines-management/add-gateway-target";
import type { ProTrialStatus } from "@common/components/ext-trial/ext-trial-logic";
import type { BuilderAsk } from "@common/components/ai-chat/transcript";
import type {
  PasskeyAckResult,
  PasskeyListResult,
  PasskeyOptionsResult,
  PasskeyRegisterVerifyParams,
  PasskeySignInOptionsResult,
  PasskeySignInStatusResult,
  PasskeyStatusResult,
  PasskeyVerifyResult,
} from "../../views/account-security/passkey-types";
import type { AuthenticationResponseJSON } from "@simplewebauthn/browser";
import type { FreeLimitPolicy } from "../free-limit-gate";
import type { CampaignFailureBreakdown } from "../../components/message-status/campaign-failure-breakdown";
import type { FirstWeekChecklist, StepId } from "../../components/first-week-card/first-week-card-logic";
import type { AgentDay1PromptOp, AgentDay1PromptState } from "../../components/agent-day1-prompt/agent-day1-prompt-logic";
import { deleteK8sEngineUrl } from "./k8s-engine-manager-url";
// Need to use the React-specific entry point to import createApi
import { BluesnapCartInfo, BluesnapErrorOutput, BluesnapShoppingContext } from "@common-models/bluesnap-cart-info";
import { Campaign, CampaignTiming } from "@common-models/campaign-model";
import type {
  CampaignEditAbortRequest,
  CampaignEditBeginRequest,
  CampaignEditBeginResult,
  CampaignEditCommitRequest,
  CampaignEditFinishResult,
  PendingCampaignEditRequest,
  PendingCampaignEditResult,
} from "@common-models/campaign-edit";
import { Invoice } from "@common-models/invoice";
import { MessageLog } from "@common-models/message-log";
import { BluesnapInvoice } from "@common-models/order";
import { Subscription } from "../../../bt-common/schemas/bt/subscription";
import { User, UserInterface } from "@common-models/user";
import { AttributionData } from "@common/utils/attributionReader";
import { taskActions } from "@common-reducers/TasksReducer";
import { PlanDescriptor, appConfig } from "@common/config/app.config";
import { LinkPreview, UserMessage } from "@bt-common/schemas/bt/user-message";
import { Drip } from "@bt-common/schemas/bt/drip";
import type { WaListenerEventEnvelope } from "@bt-common/wa-events/wa-event-relevance";
// #592c — pure `skippedSteps` arithmetic reused by the skip/unskip optimistic
// store update below (single source of truth with the drip step card). The module
// is dependency-free, so this cross-layer import pulls in no UI graph or cycle.
import { addSkippedStep, removeSkippedStep, addPendingStepSkip, removePendingStepSkip, type PendingStepSkip } from "@extension/context/content/components/campaigns-redesign/drip-step-skip-state";
import type { GetNudgeRequest, GetNudgeResponse, NudgeClientEventName, NudgeEventProps } from "@bt-common/nudges/nudge.types";
import { Settings } from "@common/types/interfaces";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { runWithRetryOn503 } from "./retry-on-503";
import { SubscriptionResponse } from 'bluesnap';
import qs from "qs";

// import { DBUserMessageThunks } from "@common-reducers/DBServiceThunks";
import { BluesnapShopper } from "@common-models/bluesnap-shopper";
import { BluesnapSubscription } from "@common-models/bluesnap-subscription";
import { ShopperInfo } from "@common-models/shopper-info";
import { AnnualSaveQuote, CancelFlowConfig, CancellationSurveyPayload } from "@common-models/cancellation";
import { setSettings } from "@common-reducers/MainReducer";
import { ArchivedOrDeletedItem } from '@common-reducers/RecycleBinReducer';
import { ConnectedSocket } from '@common-reducers/ConnectedEnginesSlice';
import { CommonRootState, AppThunkDispatch } from "@common/types/common-root-state-type";
import { UserState } from "../UserReducer";
import { waCommRequestOnQueryStarted, authenticateOnQueryStarted } from "./backend-api-query-handlers";
import { applyAuthHeader } from "./apply-auth-header";
import { withReauth } from "./base-query-reauth";
import { chargebeeApiBuilder } from "./chargebee-api";
import { recycleApiBuilder } from "./recycle-api";
import { DocumentType } from '@common/types/document-types';
import { WA_TOOL_COMMANDS } from "@/bt-common/interfaces/whatsapp-tools-commands.interface";
import { EventType } from '@common/models/node-event';
import { getClientType } from '@common/services/detect-context';
import type { AutoTopUpSetRequest, AutoTopUpView } from '@common-components/credits-usage/auto-topup-form';
// Note: MQTTSubscriber is imported dynamically to avoid circular dependency

// MCP connector client-funnel event names the dialog is allowed to report — the
// server allowlist (ALLOWED_MCP_EVENTS in mcp-event-actions.ts) is the source of
// truth; this local type just keeps the FE call sites honest. Kept local (not in
// bt-common) since no backend-shared type file exists for MCP events yet.
export type McpClientEventName =
  | "mcp_dialog_opened"
  | "mcp_platform_selected"
  | "mcp_url_copied"
  | "mcp_go_to_platform_clicked"
  // #1165 pricing-page half of the purchase journey (client-reported).
  | "mcp_pricing_shown";

// Connect-WhatsApp first-run surface funnel (activation). Mirrors McpClientEventName —
// the backend record-connect-event allowlist (connect-event-actions.ts) is the source of
// truth; this local type keeps the call sites honest. The funnel terminal
// (whatsapp_connected) is server-owned and NOT reportable here.
export type ConnectClientEventName =
  | "connect_surface_impression"
  | "connect_surface_clicked"
  | "connect_surface_dismissed";

// #651 dead-click diagnostics. Backend record-ui-diagnostic-event allowlist
// (ui-diagnostic-event-actions.ts) is the source of truth.
export type UiDiagnosticEventName = "ui_action_stalled";

export interface ImportExcelToBoardOptions {
  boardId: string;
  importDataMapping: Record<string, string>;
  selectedColumn: string;
  sheetName: string;
  importFileName: string;
  file: File;
}

export interface UpdateManyRequest<T> {
  cmd: string;
  entities: T[];
  service: string;
}

export type UpdateManyResponse<T> = T[];
export type CreateManyResponse<T> = T[];


export interface Paginated<T> {
  total: number;
  limit: number;
  skip: number;
  data: T[];
}
export interface AuthParams {
  strategy: string;
  email?: string;
  password?: string;
  accessToken?: string;
  impersonate?: string;
  /** Refer-a-friend code, read by the google-token strategy on a new signup (#186). */
  referralCode?: string;
  /** Passkey sign-in (#545): the single-use challenge handle and the browser's signed assertion. */
  challengeId?: string;
  response?: AuthenticationResponseJSON;
}

export interface GenerateUploadUrlResponse {
  uploadUrl: string;
  publicUrl: string;
  mediaId: string;
}

export interface AuthResponse {
  accessToken: string;
  user: UserInterface;
}

/**
 * Params for the `register` mutation: the new user's fields plus the signup-only
 * extras the backend accepts (attribution data + the context the signup came from).
 */
export type RegisterParams = Partial<User> & {
  attribution?: AttributionData | null;
  signup_context?: "extension" | "web_app";
  /** Refer-a-friend code from a friend's share link (#186). */
  referralCode?: string;
};

export interface ReferralSummary {
  enabled: boolean;
  code?: string;
  shareUrl?: string;
  offer?: { refereeDiscountPct: number; refereeDiscountCapCents: Record<string, number>; referrerCreditCents: Record<string, number>; holdDays: number };
  stats?: { signedUp: number; pending: number; rewarded: number; earnedCents: Record<string, number> };
}

export interface FindParams {
  service: string;
  query?: any;
}

export interface GetParams {
  service: string;
  _id: string;
}

export interface CreateParams {
  service: string;
  entity: any;
}

export interface CreateManyParams {
  service: string;
  entities: any;
}

export interface BulkDeleteParams {
  service: string;
  query: Record<string, any>;
}

export interface ResetPasswordParams {
  token: string;
  password: string;
}
export interface NextCampaignMessageResponse {
  campaign?: Campaign;
  campaignMessages: UserMessage[];
}

export interface duplicateAudienceBoardForCampaignsResponse {
  newDuplicateBoardId: string;
}

export interface duplicateTemplateForCampaignsResponse {
  newDuplicateTemplateId: string;
}

export interface MessageSentResponse {
  message: UserMessage;
  campaign?: Campaign;
  messageLog: MessageLog;
}

export interface CampaignSendTimeParams {
  sendCount: number;
  timing?: CampaignTiming;
}

export interface UserPaymentParams {
  params: string;
  plan: PlanDescriptor;
  discount: number;
  orignalPrice: number;
  subscription: SubscriptionResponse;
}

export interface GetUserPaymentParamsRequest {
  productId: string;
}

export interface CalculateCartParams {
  planId?: string; // The plan that relates to sku contract (for paypal payments)
  skuId: string;
  currency?: string;
  couponCode?: string;
  currencyConversionRateFromDollar?: number;
}

export interface ShoppingContextParams extends CalculateCartParams {
  firstName: string;
  lastName: string;
  countryCode: string;
  companyName: string;
  address: string;
  address2?: string;
  zip?: string;
  vatId: string;
  workspaceId: string;
  ccToken?: string;
}

export interface GetCheckoutPageUrlParams {
  item_price_id?: string; // Legacy support - will be deprecated
  quantity?: number; // Legacy support - will be deprecated
  coupon_ids?: string[];
  auto_detect_billing_cycle?: boolean;
  // New multi-item support
  subscription_items?: Array<{
    item_price_id: string;
    quantity: number;
  }>;
  // GA4 session linking - passed to backend so server-side purchase events join the browser session
  ga_session_id?: string;
  ga_client_id?: string;
  // ADD/MERGE intent (serenix-com/Blueticks#143). When true and the customer has an
  // existing subscription, the backend MERGES the requested item into the current
  // item list (increment if owned, append if new) instead of replacing it — so an
  // existing subscriber can stack/add credit packs. Set by the credit top-up path;
  // the plan-purchase path leaves it unset (replace semantics).
  merge_items?: boolean;
  // ONE-TIME CREDIT TOP-UP intent (serenix-com/Blueticks#143 option b). When true and the
  // customer has an existing subscription, the requested item(s) are one-time CHARGE items
  // (`{N}-Credits-USD`) appended to the current item list (plan + all add-ons kept) and
  // invoiced once; credits are granted from the item's `credits` entitlement. Repeatable,
  // so no owned-pack guard. Set by the Add-Credits top-up path.
  credit_topup?: boolean;
}

export interface GetCheckoutPageUrlResponse {
  checkoutPageUrl: string;
  debug?: {
    function: string;
    input: any;
  };
}

export interface AcceptInventionResponse {
  workspaceId: string;
  joined: boolean;
  authorized: boolean;
  error?: string;
}

export interface AcceptWorkspaceInvitationParams {
  workspaceId: string;
  inviteId: string;
}

export interface AcceptBoardInvitationParams {
  boardId: string;
  inviteId: string;
}

export interface GetItemPricesResponse {
  itemPrices: Array<{
    itemType: string;
    periodUnit: string;
    price: number;
  }>;
}

export interface ProcessDocumentParams {
  knowledgeBaseId: string;
  documentContent: string;
  documentType: DocumentType;
  documentName: string;
  metadata?: {
    author?: string;
    createdAt?: string;
    tags?: string[];
    category?: string;
    mimeType?: string;
  };
}

export interface ProcessDocumentResponse {
  success: boolean;
  documentId: string;
  processingStatus: 'completed' | 'processing' | 'error';
  metadata?: any;
  entriesCount: number;
}

export interface QueryKnowledgeBaseParams {
  knowledgeBaseIds: string[];
  documentIds?: string[];
  query: string;
  limit?: number;
  similarityThreshold?: number;
}

export interface QueryKnowledgeBaseResponse {
  results: Array<{
    content: string;
    score: number;
    metadata: {
      documentName: string;
      documentId: string;
      chunkIndex: number;
      startOffset: number;
      endOffset: number;
    };
  }>;
}

// Define FacebookPage type matching the backend definition
export interface FacebookPage {
  id: string;
  name: string;
  accessToken?: string; // Keep optional if not always needed/returned
  permissions?: string[]; // Keep optional
  hasInstagramAccount: boolean;
  instagramAccountId?: string;
  // Add profile_picture_url if you plan to fetch/use it
  profile_picture_url?: string;
}

// Define and export InstagramBusinessAccount type matching the backend definition
export interface InstagramBusinessAccount {
  id: string;
  name: string;
  username: string;
  profile_picture_url?: string;
  followers_count?: number;
  facebookPageId: string;
  pageAccessToken?: string; // Keep optional
  messagingEnabled?: boolean;
}

export interface GetPagesResponse {
  facebookPages: FacebookPage[]; // Use the defined type
  instagramAccounts: InstagramBusinessAccount[]; // Use the exported type
}

export interface BtWaCommRequestParams {
  contactIds?: string[];
  chatId?: string;
  msgId?: string;
  waMsg?: any;
  type: string;
  uploadUrl?: string;
  publicUrl?: string;
  /** Optional engine socket ID to route the request to a specific connected engine */
  engineId?: string;
  /** Optional reason for certain operations like reloadPage */
  reason?: string;
  options?: {
    limit?: number;
    offset?: number;
    searchFilter?: string;
  }
}



export interface CreditUsageData {
  planName: string;
  totalCredits: number;
  creditsUsed: number;
  creditsRemaining: number;
  percentageUsed: number;
  /** The monthly allowance in force (BE#891). Optional: older backends omit it. */
  monthlyAllowance?: number;
  /** Credits used beyond the allowance + top-ups; 0 unless overdrawn (BE#891). */
  creditsOverage?: number;
  renewalDate: string;
  daysRemaining: number;
}

export interface UsageDataPoint {
  date: string;
  usage: number;
}

export interface BotUsageData {
  id: string;
  name: string;
  /** Charged credits: the bar's value for bots and agents alike. */
  credits: number;
  /** #38: legacy bots are 'bot'; Agents 2.0 agents are 'agent' (older backends send neither). */
  kind?: 'bot' | 'agent';
  botId?: string;
  agentId?: string;
  /** Agents only: credits estimated for runs that were not charged. */
  estimatedCredits?: number;
  runs?: number;
  chargedRuns?: number;
}

export interface CreditUsageResponse {
  creditUsageData: CreditUsageData;
  usageDataPoints: UsageDataPoint[];
  botUsageData: BotUsageData[];
}

export interface ExportCreditUsageResponse {
  url: string;
}

export interface SubscribeToPageMessagingRequest {
  pageId: string;
  pageAccessToken: string;
}

export interface SubscribeToPageMessagingResponse {
  success: boolean;
  message: string;
}

export interface UnsubscribeFromPageMessagingRequest {
  pageId: string;
  pageAccessToken: string;
}

export interface UnsubscribeFromPageMessagingResponse {
  success: boolean;
  message: string;
}

export interface SendInstagramMessageRequest {
  recipientId: string;
  pageId: string;
  text: string;
  assetUrl?: string;
}

export interface SendInstagramMessageResponse {
  success: boolean;
  messageId?: string;
  error?: string;
}

export interface RevokeTokenRequest {
  tokenType?: 'facebook' | 'instagram';
  permission?: string;
}

export interface RevokeTokenResponse {
  success: boolean;
  message: string;
}

export interface RefreshTokenRequest {
  tokenId: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  message: string;
  token?: {
    tokenType: string;
    expiresAt: string;
    timeGainedHours?: number;
  };
}

export interface ValidateFacebookTokenResponse {
  isValid: boolean;
  tokenId?: string;
  message?: string;
}

export interface ValidateFacebookTokenParams {
  token: string;
}

// Dashboard data interfaces
export interface DashboardOverviewStats {
  totalAgents: number;
  deployedAgents: number;
  draftAgents: number;
  activeAgents: number;
  totalSessions: number;
  averageNodesPerAgent: number;
  botUsageGrowth: number;
}

export interface NodeTypeDistributionItem {
  name: string;
  value: number;
  color: string;
}

export interface FeatureAdoptionItem {
  name: string;
  value: number;
}

export interface KnowledgeBaseAdoptionStats {
  totalKBs: number;
  totalDocuments: number;
  agentsUsingKB: number;
  percentAgentsUsingKB: number;
}

export interface BotListItem {
  _id: string;
  name: string;
  description: string;
  owner: string;
  activeDeploymentId: string | null;
  botStats?: {
    activeSessions: number;
    todaySessions: number;
    avgDuration: string;
  };
  createdAt?: Date;
  updatedAt?: Date;
}

export interface NodeEventStats {
  totalEvents: number;
  totalErrors: number;
  eventsByType: Record<EventType, number>;
  processTypeDistribution: Record<string, number>; // Use string if ProcessType import causes issues
  totalCreditUsage: number;
}

export interface AllDashboardStats {
  overviewStats: DashboardOverviewStats;
  nodeTypeDistribution: NodeTypeDistributionItem[];
  advancedFeatureAdoption: FeatureAdoptionItem[];
  kbAdoption: KnowledgeBaseAdoptionStats;
  nodeEventStats: NodeEventStats;
}

export interface AutomationSession {
  _id: string;
  botId: string;
  botVersionId?: string;
  status: 'active' | 'completed' | 'error';
  createdAt: string;
  updatedAt: string;
  variables?: Record<string, any>;
  metadata?: Record<string, any>;
  owner?: string;
}

export interface FindSessionsByNodeEventParams {
  eventType?: EventType;
  processType?: string;
  limit: number;
  skip: number;
}

// Interface for the getAllSessions endpoint parameter
export interface GetAllSessionsParams {
  limit?: number;
  skip?: number;
  status?: string;
  sortBy?: string;
  sortOrder?: 1 | -1;
  search?: string;
}

export interface GetDashboardMessagesParams {
  status?: string;
  limit?: number;
  startDate?: string;
  showLateOnly?: boolean;
  contactId?: string;
  ownerId?: string;
}

export interface GetDashboardMessagesResponse {
  data: UserMessage[];
}

// Interface for generating supported user token
export interface GenerateSupportedUserTokenParams {
  supportedUserId: string;
}

export interface GenerateSupportedUserTokenResponse {
  success: boolean;
  accessToken: string;
  userId: string;
  userEmail: string;
}

// Interface for getting user connected clients from EMQX
export interface GetUserConnectedClientsParams {
  userId: string;
}

// Bot list interface for admin-only endpoint

// #1024 - result of chargebee.getGatewayImpact.
export interface GatewayImpact {
  hasGateway: boolean;
  pendingCount: number;
}

export const initAppData = createAsyncThunk(
  "initAppData",
  async (_, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const state = getState() as CommonRootState;

    // Check if settings already exist in state
    if (state.MainReducer?.settings) {
      return;
    }

    const settings = await dispatch(getSettings.initiate()).unwrap()
    if (settings.freeLimits) dispatch(taskActions.setFreeLimits(settings.freeLimits))
    dispatch(setSettings(settings))
  }
);

export type TransitionData = {
  sessionId: string;
  transitions: Record<string, number>;
  lastProcessedTimestamps: Record<string, string>;
  fromEvent: string;
};

//console.log(appConfig)



export const apiBaseQuery = fetchBaseQuery({
  baseUrl: appConfig.API_ENDPOINT,
  prepareHeaders: async (headers: Headers, { getState, endpoint }) => {
    const userState: UserState = (getState() as CommonRootState).UserReducer;
    const supportedUserId = new URLSearchParams(window.location.search).get('supportedUserId');
    const supportedWorkSpaceId = new URLSearchParams(window.location.search).get('workspaceId');

    if (endpoint !== 'importExcelToBoard' && endpoint !== 'createAndDeleteAutomation') {
      headers.set("Content-Type", "application/json");
    }
    // wa-scheduler#129: redux auth may never hydrate (e.g. a gateway engine
    // whose boot-time POST /authentication raced a backend rollout). Without a header,
    // every request 401s silently — dropping ~40% of inbound
    // wa-message-listener-event POSTs fleet-wide. applyAuthHeader falls back to
    // the persisted token from the authoritative hub the gateway seeds. The
    // lazy import breaks the auth-service <-> backend-api module cycle.
    await applyAuthHeader(headers, userState?.accessToken, async () => {
      const { authService } = await import("@/common/bt-auth/auth-service");
      return authService.getPersistedToken();
    });
    if (userState?.socketId) {
      headers.set('Socket', userState.socketId);
    }

    if (supportedUserId) {
      headers.set('supportedUserId', supportedUserId);
    }
    if (supportedWorkSpaceId) {
      headers.set('supportedWorkSpaceId', supportedWorkSpaceId);
    }

    // Add MQTT client info to headers for campaign creation tracking
    try {
      // Dynamic import to avoid circular dependency
      const { MQTTSubscriber } = await import(/* webpackMode: "eager" */ '@common/noifier-module/MQTTSubscriber');
      const mqttSubscriber = MQTTSubscriber.getInstance();
      const clientId = mqttSubscriber.getCurrentClientId();
      const clientType = mqttSubscriber.getCurrentClientType() || getClientType();

      if (clientId) {
        headers.set('X-Client-Id', clientId);
      }
      if (clientType) {
        headers.set('X-Client-Type', clientType);
      }
    } catch (error) {
      // MQTT not available, continue without client info
    }

    return headers;
  },
});
export type ApiBaseQuery = typeof apiBaseQuery;

/** Refer-a-friend (#426): where a share link was shown, matching the card's `variant`. */
export type ReferralSurface = "menu" | "page" | "inline";

/** Anonymous referral funnel events; the backend action `referral.track` (no session needed). */
export type ReferralTrackPayload =
  | { kind: "visited"; code: string; visitorKey: string }
  | { kind: "copied"; surface: ReferralSurface }
  | { kind: "shared"; surface: ReferralSurface; channel: "whatsapp" };

/**
 * Fire-and-forget referral tracking. A plain fetch rather than an RTK endpoint so it works from
 * the non-React capture path and without a session. Never throws, never blocks, swallows failures.
 */
export function trackReferralLink(payload: ReferralTrackPayload): void {
  try {
    const base = String(appConfig.API_ENDPOINT || "").replace(/\/+$/, "");
    if (!base || typeof fetch !== "function") return;
    void fetch(`${base}/actions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ cmd: "referral.track", ...payload }),
      credentials: "omit",
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // tracking must never surface an error
  }
}

// wa-scheduler#129 (fix #2): the access token is a permanent JWT (backend
// jwtOptions.expiresIn = "100y") persisted in the authoritative chrome.storage
// hub. At boot a request can fire BEFORE redux hydrates AND before the MV3
// service worker answers `get-token`, so prepareHeaders attaches no token and
// the request 401s. The fire-and-forget wa-message-listener-event POST has no
// retry, so that inbound event is dropped forever and only self-heals on a
// lucky reboot — the ~2% residual left after the prepareHeaders hub-fallback
// (#1). `baseQueryWithReauth` re-reads the hub (via attemptJwtAuth, which also
// repopulates redux on success) and retries once (see base-query-reauth.ts).
const REAUTH_HUB_SEED_BACKOFF_MS = 600;
const forceTokenRehydration = async (dispatch: AppThunkDispatch): Promise<void> => {
  // Lazy import breaks the auth-service <-> backend-api module cycle.
  const { authService } = await import("@/common/bt-auth/auth-service");
  const authed = await authService.attemptJwtAuth(dispatch, { force: true });
  if (!authed) {
    // The hub had no token yet (MV3 SW still waking / not seeded). Brief backoff
    // so it can seed before the single retry re-reads it via prepareHeaders —
    // widens the recovery window for the exact boot race this fixes.
    await new Promise((resolve) => setTimeout(resolve, REAUTH_HUB_SEED_BACKOFF_MS));
  }
};

export const baseQueryWithReauth: typeof apiBaseQuery = withReauth(apiBaseQuery, forceTokenRehydration);

export const tagTypes = [
  "user-messages",
  "templates",
  "lists",
  "contacts",
  "labels",
  "tasks-extra-data",
  "tasks",
  "task-groups",
  "boards",
  "message-log",
  "campaigns",
  "ApiKeys",
  "PausedSeries",
  // The six session / k8s-engine mutations below already declare
  // `invalidatesTags: ['Session']`. Without the tag declared here RTK Query drops the
  // invalidation and warns "Tag type 'Session' was used, but not specified in
  // `tagTypes`!" on every create/delete/restart. No query PROVIDES 'Session' yet
  // (the engines page refetches by hand via DBSessionThunks.find), so declaring it is
  // behaviour-neutral today and makes the existing invalidations wire up correctly the
  // moment a query starts providing it.
  "Session",
  // My Bridge (self-service Claude-Code WhatsApp agents). The overview query
  // provides 'MyBridge'; the create/patch/delete mutations invalidate it so the
  // agents list refetches after a change.
  "MyBridge",
  // One agent's per-chat rows: the inspector's chat-sessions AND the rev 3.3
  // takeover conversations. ONE tag, so the takeover / fresh-conversation /
  // reset-sessions actions refresh both panels together (they show one state).
  "MyBridgeSessions",
  "MyBridgeKnowledge",
  "MyBridgeMcpServers",
];

export type ApiTagTypes = typeof tagTypes[number];

export interface WorkspaceInvoices {
  invoiceList: BluesnapInvoice[];
  chargebeeTransactionList: ChargebeeTransaction[];
}

// Define GenerateAIResponse interfaces
export interface GenerateAIResponseParams {
  prompt: string;
  currentText: string;
  chatContext?: any[];
  chatInfo?: {
    id: string;
    name: string;
    isGroup: boolean;
  };
  userLanguage?: string;
}

export interface GenerateAIResponseResult {
  success: boolean;
  text?: string;
  error?: string;
}

/**
 * Rate-limit usage attached to every api-key row by `internal/api-keys`.
 *
 * NOTE: `keyedOn: 'account'` means the quota is SHARED across every key the
 * user owns — minting another key does not grant a fresh budget. Every field
 * is optional-safe: treat a missing `usage` as "unknown" and render nothing.
 */
export interface ApiKeyUsage {
  keyedOn: 'account';
  used: number;
  limit: number;
  remaining: number;
  windowSeconds: number;
  windowDescription: string;
  resetAt: string;
  rateLimited: boolean;
  unlimited: boolean;
  plan: 'free' | 'paid' | 'unknown';
}

export interface ApiKeyRow {
  _id: string;
  workspaceId: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  environment: 'live' | 'test';
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  isLegacy?: boolean;
  usage?: ApiKeyUsage;
}

export interface ApiKeyCreateResponse extends ApiKeyRow {
  fullKey: string;
}

// ── My Bridge (self-service Claude-Code WhatsApp agents) ────────────────────
// The end-user surface for managing their bridge + agents. Types mirror the
// backend /my-bridge contract exactly (Feathers REST: overview/chats = find,
// create = POST, patch = PATCH /:id, remove = DELETE /:id).
/** `path?k=v&…` from the params that are set (undefined/'' are left out). */
const withQuery = (path: string, params: Record<string, string | number | undefined>): string => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') q.set(k, String(v)); });
  const qs = q.toString();
  return qs ? `${path}?${qs}` : path;
};
/** `my-bridge/agents/:agentId[/rest][?params]`. */
const myBridgeAgentUrl = (agentId: string, rest = '', params: Record<string, string | number | undefined> = {}): string =>
  withQuery(`my-bridge/agents/${encodeURIComponent(agentId)}${rest ? `/${rest}` : ''}`, params);

export interface MyBridge {
  id: string;
  label: string;
  status: string;
  ownerId?: string;
}

/**
 * rev 3.3 §6.17.1 — Projects/connections are gone: the account IS the tenant.
 * A few legacy endpoints (the cloud staging bridge, `my-bridge/chats`,
 * `my-bridge/agent-schedules` create) still require a `clientId`; they get this
 * constant — every agent on every account is on the implicit 'default' project.
 */
export const MY_BRIDGE_LEGACY_CLIENT_ID = 'default';

export interface MyBridgeWebAccess {
  mode: 'off' | 'allowlist' | 'all';
  hosts: string[];
}

export interface MyBridgeKnowledgeFile {
  name: string;
  bytes?: number;
  size?: number;
  updatedAt?: string;
}

export interface MyBridgeAgent {
  id: string;
  /** ISO time of the agent row's last write (the bridge stamps it on create + every update). */
  updatedAt?: string;
  /** Legacy (rev 3.3 §6.17.1 removes it) — may still be present on older backends; never shown. */
  clientId?: string;
  label: string;
  enabled: boolean;
  chatIds: string[];
  chatScope?: string;
  /** The BLOCK list when chatScope='all_except' (chatIds is empty then). */
  chatBlockList?: string[];
  includeNewChats?: boolean;
  /** 'all' (default, absent = all) = private chats and groups; 'private' = never groups. */
  chatTypes?: 'all' | 'private';
  /** WHERE it may send (separate from its trigger chats); absent = 'same'. */
  sendScope?: 'same' | 'list' | 'all_except';
  sendChatIds?: string[];
  identityPrompt: string;
  knowledgeText?: string;
  knowledgeOnly?: boolean;
  stayOnTopic?: boolean;
  // Operator-only settings (see MyBridgeAgentInput).
  model?: string;
  effort?: string;
  transcribeLang?: string;
  hideBotPrefix?: boolean;
  /** #464 silent weekly windows (the agent ignores inbound inside them); null/absent = never quiet. */
  quietHours?: MyBridgeQuietHours | null;
  /** rev 3.3 takeover DEFAULTS, inherited by new conversations (then independent per conversation). */
  takeover?: MyBridgeTakeoverDefaults;
  /** #1059 — minutes (1-1440) after the owner's last message in an AUTOMATIC takeover before the agent resumes; null/absent = OFF. */
  autoResumeAfterMinutes?: number | null;
  /** rev 3.3 operator chat (notices, questions, start/stop/continue); null = none. */
  operatorChatId?: string | null;
  /** Web access (WebFetch). Absent/off = no internet. See the Bridge web-access module. */
  webAccess?: MyBridgeWebAccess;
}

export type MyBridgeQuietDay = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export interface MyBridgeQuietWindow {
  days: MyBridgeQuietDay[];
  /** HH:mm, 24h. A window with to < from crosses midnight into the next day. */
  from: string;
  to: string;
}
/** #464 — an Agent's quiet hours: silent windows in an IANA zone. */
export interface MyBridgeQuietHours {
  tz: string;
  windows: MyBridgeQuietWindow[];
}

/** rev 3.3 §6.7 — an Agent's takeover defaults. */
export interface MyBridgeTakeoverDefaults {
  /** Human takeover is allowed at all. */
  enabled: boolean;
  /** A message the owner writes in the chat arms the takeover automatically. */
  byIncomingMessage: boolean;
}

/** #1029 — why the agent is paused in a chat: the owner wrote from their number, or paused it by hand. */
export type MyBridgeTakeoverReason = 'auto_human_typed' | 'manual';

/** #1029 — the "Paused" badge fields the backend adds to a conversation's `takeover` (`since` is shared with the legacy state). */
export interface MyBridgeTakeoverBadge {
  /** Optional: a backend that predates #1029 omits all three. */
  paused?: boolean;
  reason?: MyBridgeTakeoverReason | null;
  /** When the agent resumes on its own; null = paused until someone resumes it. */
  until?: string | null;
}

/** One rev 3.3 `agent_conversations` row with its takeover state (GET my-bridge/agents/:id/agent-conversations). */
export interface MyBridgeAgentConversationRow {
  conversationId: string;
  agentId: string;
  chatId: string;
  sessionId: string;
  takeover: MyBridgeTakeoverDefaults & { status: boolean; since: string | null; source: 'auto' | 'manual' | null } & MyBridgeTakeoverBadge;
  lastActivityAt: string | null;
  createdAt: string | null;
}
export interface MyBridgeAgentConversationsPage { conversations: MyBridgeAgentConversationRow[]; hasMore: boolean }
/** PATCH body: the live takeover and/or this conversation's own flags. */
export interface MyBridgeConversationTakeoverPatch { status?: boolean; enabled?: boolean; byIncomingMessage?: boolean }

/**
 * An agent's public Tik profile (backend services/tiks; docs/tiks-plan.md v3). The
 * content mirrors the website's `TikContent`; `status` is derived by the backend on
 * every read (live = enabled ∧ all chats ∧ incoming on ∧ one connected number).
 */
export type TikShape = 'round' | 'squircle' | 'egg' | 'gumdrop' | 'bun';
export type TikAccessory =
  | 'headphones' | 'sunnies' | 'polish' | 'scissors' | 'partyHat' | 'polaroid' | 'swatches'
  | 'tattooKit' | 'chefHat' | 'nightcap' | 'gradCap' | 'saladBowl' | 'sweatband' | 'sprout';
export type TikCategory = 'Create' | 'Look' | 'Health' | 'Home' | 'Family' | 'Learn';
export interface TikChatLine { from: 'user' | 'tik'; text?: string; artifact?: { kind: string; title: string; subtitle?: string } }
export interface TikContent {
  handle: string;
  name: string;
  profession: string;
  tagline: string;
  bio: string;
  services: string[];
  category: TikCategory;
  look: { color: string; shape: TikShape; accessory: TikAccessory; sleepy?: boolean };
  starter: string;
  sampleChat: TikChatLine[];
}
export type TikMissing = 'enabled' | 'all_chats' | 'incoming' | 'number' | 'phone_consent' | 'published' | 'approval';
export interface AgentTik {
  tik: (TikContent & { published: boolean; review: 'pending' | 'approved' | 'rejected'; phoneConsentAt?: string }) | null;
  status: { live: boolean; listed: boolean; missing: TikMissing[] } | null;
}

/** An agent's Google Calendar (backend agent-calendar). The service-account key is write-only — never returned. */
export type MyBridgeAgentCalendar =
  | { connected: false }
  | { connected: true; calendarId: string; timeZone: string; serviceAccountEmail: string };
/** `POST …/calendar/test` — a 2-day free/busy probe: ok, or a plain reason + what to do. */
export type MyBridgeAgentCalendarTest = { ok: true } | { ok: false; reason: string; hint: string };

/** One tool of an external MCP server (connect-time snapshot; description is THIRD-PARTY text — render as plain text). */
export interface McpToolSnapshot { name: string; description?: string; readOnly?: boolean; destructive?: boolean }
/** BE#1317: a per-contact cap on one tool. */
export type McpLimitWindow = 'day' | 'week' | 'month' | 'lifetime';
export interface McpToolLimit { tool: string; max: number; window: McpLimitWindow }
export interface McpToolPolicy { deny: string[]; limits?: McpToolLimit[] }
/** An agent's external MCP server (backend agent-mcp-servers). The credential is write-only — never returned. */
export interface MyBridgeMcpServer {
  id: string;
  name: string;
  preset?: string;
  url: string;
  /** How it authenticates (a reconnect of a 'key' server asks for a new key). */
  auth?: 'key' | 'oauth' | 'none';
  connected: boolean;
  tools: McpToolSnapshot[];
  /** The ONLY tools the agent may call (all off by default). */
  enabledTools: string[];
  /** BE#1317: owner-set "never" tools and per-contact caps. */
  toolPolicy?: McpToolPolicy;
  /** BE#1317: which parts of the policy the backend lets this owner set now (flags); absent = none, so the form shows no controls. */
  policyAvailable?: { deny: boolean; limits: boolean };
  lastError?: string;
  /** Display, from its library entry (absent for a custom URL). */
  title?: string;
  icon?: string;
  /** A sign-in link waiting for someone else (the link itself is shown once, when sent). */
  invite?: { expiresAt: string };
}
/** A library entry (backend presets.ts — featured services + the Claude directory + the MCP Registry). Texts are THIRD-PARTY. */
export interface McpPreset {
  id: string;
  name: string;
  url: string;
  /** 'key' = the user pastes an API key / token. Absent while unavailable. */
  auth?: 'oauth' | 'key' | 'none';
  keyHelpUrl?: string;
  summary?: string;
  description?: string;
  by?: string;
  icon?: string;
  verified?: boolean;
  /** false = listed, not connectable yet ("coming soon"). */
  available?: boolean;
  categories?: string[];
  tools?: string[];
}
/** The library's search: no `q` and no `category` = the featured + popular services; `limit` defaults to 60 (max 600). */
export interface McpPresetQuery {
  q: string;
  category?: string;
  limit?: number;
}
/** Connect body: `server` = a library id or an MCP URL (+ optional key). */
export interface ConnectMcpServerParams {
  agentId: string;
  server: string;
  apiKey?: string;
}
/** PATCH body: finish a sign-in ({code,state} from the popup) | reconnect | send the sign-in as a link | re-read tools | set the grant. */
export type PatchMcpServerParams = { agentId: string; id: string } & (
  | { code: string; state: string }
  | { reconnect: true; apiKey?: string }
  | { invite: true }
  | { refreshTools: true }
  | { enabledTools: string[] }
  | { toolPolicy: { deny?: string[]; limits?: McpToolLimit[] } }
);
/** Connect / reconnect result: connected now, an `authorizationUrl` to open in the popup, or (sent as a link) the `inviteUrl` — returned this once. */
export interface ConnectMcpServerResult { server: MyBridgeMcpServer; authorizationUrl?: string; inviteUrl?: string }

/** A board an agent works on (backend services/agent-boards). */
export interface MyBridgeAgentBoard { id: string; name: string; kind: string }

/** One voice an agent can speak with (backend agent-voice/voices.ts). */
export interface MyBridgeVoiceOption { id: string; gender: 'female' | 'male'; style: string }
/** The voices of the voice provider in use (GET my-bridge/voices). */
export interface MyBridgeVoiceCatalog { provider: string; defaultVoice: string; voices: MyBridgeVoiceOption[] }

/** An agent's deterministic, server-enforced capability contract (see backend agent-policy). */
export interface MyBridgeAgentPolicy {
  agentId: string;
  /** Incoming-message trigger. enabled:false = time-triggered only. */
  incoming: { enabled: boolean };
  /** Tool access; whatsappMcp:false = no Blueticks MCP minted (the agent only returns replies). */
  tools: { whatsappMcp: boolean; voice?: boolean; calls?: boolean; boards?: boolean; /** Creative tools: songs (Suno) / images, made into its own chat. */ suno?: boolean; images?: boolean };
  /** When to answer with a voice note instead of text (the owner's words; absent = the default rule). */
  voiceWhen?: string;
  /** The voice it speaks with — voice notes and calls (an id from GET my-bridge/voices; absent = the default). */
  voiceName?: string;
  /** The agent's own boards (tools.boards): kind = 'tasks' / 'leads' / 'invoices' / 'custom:<slug>'. */
  boards?: MyBridgeAgentBoard[];
  /** The account's boards, for "use an existing board for …". */
  accountBoards?: { id: string; name: string }[];
  /**
   * READ scope for the READ-ONLY MCP (sending is the Bridge's job). `messages` = may
   * read messages of its OWN chat (the conversation it answers / a schedule's source
   * chat) plus `messageChatIds` — never any chat (F7, server-enforced). Absent =
   * nothing readable.
   */
  read?: MyBridgeReadPolicy;
}

export interface MyBridgeReadPolicy {
  /** Legacy: stored, but never granted to an agent session (the chat list spans every chat). Not sent. */
  chats?: boolean;
  messages: boolean;
  messageChatIds: string[];
}
/** What the form saves: no `chats` (ignored by the backend since F7). */
export type MyBridgeReadPolicyInput = Omit<MyBridgeReadPolicy, 'chats'>;

/** Structured recurrence for an agent time-trigger (matches the backend agent-schedule model). */
export interface MyBridgeScheduleRecurrence {
  freq: 'hourly' | 'daily' | 'weekly' | 'monthly';
  /** For hourly this is the ANCHOR the interval counts from, not the only firing hour. */
  hour: number;
  minute: number;
  /** Every N hours (1..24, default 1), for hourly. */
  interval?: number;
  /** 0=Sun..6=Sat, for weekly. */
  weekdays?: number[];
  /** 1..31, for monthly. */
  monthday?: number;
  timezone: string;
}

/** One agent time-trigger (an agent-schedule row). */
export interface MyBridgeSchedule {
  _id: string;
  agentId: string;
  /** Legacy (rev 3.3 §6.17.1) — ignored by the UI. */
  clientId?: string;
  label: string;
  enabled: boolean;
  recurrence: MyBridgeScheduleRecurrence;
  /** What the agent should do when it fires. */
  prompt: string;
  /** The chat whose messages the run reads. */
  sourceChatId: string;
  /** Where the result is sent (empty = reply in the source chat). */
  destChatId?: string;
  nextRunAt?: string;
  /** #47: the agent itself is paused, so this trigger won't fire (absent on older backends). */
  agentStopped?: boolean;
}

/** The editable fields of a time-trigger (create body; a patch sends any subset). */
export interface MyBridgeScheduleInput {
  label?: string;
  recurrence: MyBridgeScheduleRecurrence;
  prompt: string;
  sourceChatId: string;
  /** Empty = reply in the source chat. */
  destChatId?: string;
  enabled?: boolean;
}

export interface MyBridgeOverview {
  provisioned: boolean;
  configured: boolean;
  /** True when the current user is a Bridge operator/admin (sees all bridges). */
  isAdmin: boolean;
  bridge: MyBridge | null;
  // rev 3.3 §6.17.1: the `projects` key is removed (older backends may still
  // send it — it is simply ignored).
  agents: MyBridgeAgent[];
  /** Per-agent capability contract, keyed by agentId. */
  policies?: Record<string, MyBridgeAgentPolicy>;
  /** Every time-trigger for the user's agents (filter by agentId in the UI). */
  schedules?: MyBridgeSchedule[];
  /** Admin-only: every bridge across all owners. Empty for a regular user. */
  allBridges: MyBridge[];
}

export interface MyBridgeChat {
  // The endpoint returns chats keyed by `id` (matches the contact picker's
  // SelectedContact.id); `chatId` is kept optional for older/other callers.
  id?: string;
  chatId?: string;
  name?: string;
  chatType?: string;
}

// ── Bridge Inspector shapes (mirror the Bridge dashboard, permissive) ─────────
export interface MyBridgeChatSessionRow {
  /** rev 3.3: unique row key (conversation id, or the session id of a conversation-less schedule/UI session). */
  id?: string;
  /** rev 3.3: the session profile (`agent`, `schedule`, …). Always set on v2 rows, absent on legacy bridge rows. */
  profile?: string;
  /** The conversation's chat / a schedule run's target chat; null when unknown. */
  chatId: string | null;
  sessionId: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  /** The pod/host that last ran this chat's Claude session (k8s pod name). */
  pod?: string | null;
  /** null = a session with no conversation (schedule run): nothing to take over or reset. */
  takeover?: { held: boolean; since: string | null; source: string | null } | null;
  /** rev 3.3: a schedule occurrence session — its occurrence and recorded outcome. */
  schedule?: { occurrenceId: string; outcome: { status: string; reason: string | null } | null } | null;
  /** LEGACY (flag-off bridge rows only; deleted at V2 cutover): per-chat outcome counts. */
  outcomes?: { counts?: Record<string, number>; total?: number };
  session?: { attempted?: boolean; open?: boolean; closedAt?: string | null };
  /** rev 3.3: the latest give-up (delivery_failed / resume_failed) or recorded operator_notice (`kind`) the owner must see. */
  failure?: { name: string; kind?: string | null; at: string | null } | null;
}
export interface MyBridgeAgentChatSessions {
  agentId: string;
  count: number;
  outcomes?: Record<string, number>;
  sessions: MyBridgeChatSessionRow[];
}
export interface MyBridgeConversationMessage { id?: string; chatId?: string; text?: string; fromMe?: boolean; timestamp?: number; type?: string; ack?: number; senderName?: string }
export interface MyBridgeAgentConversation { messages: MyBridgeConversationMessage[]; hasMore?: boolean; nextSkip?: number }
export interface MyBridgeActivityRow { at?: string | null; type: string; level: 'presence' | 'reply' | 'status' | 'debug'; chatId?: string | null; tool?: string | null; target?: string | null; ms?: number | null; note: string }
export interface MyBridgeAgentActivity { events: MyBridgeActivityRow[]; level?: string; source?: string }
/** One Claude session turn: a message, a tool call/result, or the model's thinking. */
export interface MyBridgeTranscriptTurn {
  role: 'user' | 'assistant' | 'system' | 'tool' | 'thinking';
  text: string;
  at?: string | null;
  toolName?: string;
  /** Builder chat (backend release 11): the tools an assistant turn used, raw names in call order. */
  toolCalls?: { tool: string }[];
  /** User turns (Task 7): the files attached to this turn — name + mime only, never a URL/id. */
  attachments?: { name: string; mime: string }[];
  /** Builder chat (#813): the conversation this turn belongs to — an agent's history spans several. */
  conversationId?: string;
  agentId?: string;
  /** Assistant turns (#813 step 4): a confirm card — update the existing agent / create a new one. */
  ask?: BuilderAsk;
}
export interface MyBridgeAgentTranscript { sessionId: string | null; turns: MyBridgeTranscriptTurn[]; error?: string }
export interface MyBridgeInspectBridge { bridgeId: string; agents: MyBridgeAgent[]; runtimeReachable?: boolean }

// Shape accepted by both create (POST) and patch (PATCH). On patch every field
// is optional, so callers pass only what changed (e.g. `{ enabled: true }`).
export interface MyBridgeAgentInput {
  label: string;
  identityPrompt: string;
  chatIds: string[];
  /** 'list' = answer only chatIds; 'all_except' = answer all chats except chatBlockList. */
  chatScope?: string;
  chatBlockList?: string[];
  /** Also answer brand-new incoming chats. */
  includeNewChats?: boolean;
  /** 'private' = one-to-one chats only (never groups); 'all' (default) = both. */
  chatTypes?: 'all' | 'private';
  /**
   * Transient validation signal (NOT stored on the Bridge agent): false relaxes the
   * create-time "at least one chat" rule for a time-triggered-only agent. The empty
   * chatIds is what actually turns incoming off. The persisted flag lives on the
   * agent-policy, not here.
   */
  incomingEnabled?: boolean;
  knowledgeText?: string;
  knowledgeOnly?: boolean;
  stayOnTopic?: boolean;
  topicScope?: string;
  enabled?: boolean;
  // Operator-only (admin form only; the Bridge 403s a non-operator that sends them).
  model?: string | null;
  effort?: string | null;
  transcribeLang?: string | null;
  hideBotPrefix?: boolean;
  /** #464 quiet hours; null turns them off. */
  quietHours?: MyBridgeQuietHours | null;
  /** rev 3.3 takeover defaults (partial = patch field by field). */
  takeover?: Partial<MyBridgeTakeoverDefaults>;
  /** #1059 auto-resume window in minutes (1-1440); null turns it off. */
  autoResumeAfterMinutes?: number | null;
  /** rev 3.3 operator chat; null clears it. */
  operatorChatId?: string | null;
  /** Web access (WebFetch): off = no internet, allowlist = only `hosts`, all = any site. */
  webAccess?: MyBridgeWebAccess | null;
  /** WHERE it may send (separate from its trigger chats): 'same' = reply where triggered. */
  sendScope?: 'same' | 'list' | 'all_except';
  sendChatIds?: string[];
}

/** Create = the agent input + the integrations chosen in the new-agent form (library ids / MCP URLs; attached unconnected, best-effort). */
export type CreateMyBridgeAgentParams = MyBridgeAgentInput & { mcpServers?: string[] };
export type UpdateMyBridgeAgentParams = { id: string } & Partial<MyBridgeAgentInput>;

/** Args for one natural-language agent-builder turn (POST my-bridge/agents/build/jobs). */
export interface BuildMyBridgeAgentParams {
  /** What the user typed — the description (or the answer to a prior question). May be
   *  empty only when `attachments` is non-empty (Task 7). */
  prompt: string;
  /** Continues the same Claude build session across an ask→answer turn. */
  resumeSessionId?: string;
  /** Uploaded file ids (`UploadedAttachment.uploadId`) from `POST ai-chat/uploads`. */
  attachments?: string[];
}
/**
 * One builder turn's outcome (mirrors the runtime `AgentBuildResult`):
 *  - `created` the agent was made (disabled/scoped) — open it in the Edit dialog.
 *  - `ask`     the builder needs more info — show `reply`, send the answer with `sessionId`.
 *  - `updated` an Edit Agent chat turn changed the agent (`message` = an optional notice).
 *  - `error`   the turn failed/was refused (`message`); `sessionId` when still resumable.
 *  - `stopped` the user pressed Stop (`message` is always "Stopped."); `sessionId` stays so
 *    the next message resumes the same session.
 */
export type MyBridgeAgentBuildResult =
  | { kind: 'created'; agent: MyBridgeAgent; sessionId: string }
  | { kind: 'ask'; reply: string; sessionId: string; asks?: BuilderAsk[] }
  | { kind: 'updated'; reply: string; sessionId: string; message?: string; asks?: BuilderAsk[] }
  | { kind: 'error'; message: string; sessionId?: string }
  | { kind: 'stopped'; message: string; sessionId: string };

/** The Edit Agent chat: the agent's builder conversation (GET my-bridge/agents/:id/builder-chat). */
export interface MyBridgeAgentBuilderChat {
  agentId: string;
  /**
   * The agent's CURRENT edit conversation (F6) — the one `turns` belong to; null = none yet.
   * Every Edit chat is its own conversation: the chat shows `turns` only when this is the
   * conversation it has open.
   */
  conversationId?: string | null;
  turns: MyBridgeTranscriptTurn[];
  /** A turn still running (reconnect after a refresh) — poll it as a build job. */
  runningJobId: string | null;
  /** False when the AI builder is not configured on this backend. */
  available: boolean;
}

/** A polled async build job. `result` is the outcome once `status` is done/error. */
export interface MyBridgeBuildJob {
  jobId: string;
  status: 'running' | 'done' | 'error' | 'unknown' | 'stopped';
  result?: MyBridgeAgentBuildResult;
}

// #592c — optimistically apply a single-step skip/unskip to the local drip in the
// dbService store, so the step-card "Skipped" badge AND the calendar chip flip
// INSTANTLY — without a page reload and WITHOUT depending on the realtime (MQTT)
// drip echo. The echo is not guaranteed to reach this client (a local-dev broker
// mismatch, or a dropped/late event); the reliable path is this client-side write
// (the server-verified version bump + array only help IF the patch is delivered).
//
// It writes the SAME field the backend does, chosen by the occurrence:
//   - CURRENT cycle  (occurrenceDate == the drip's schedule.dueDate, minute
//     granularity) → `skippedSteps` (add/remove the step index). This is the
//     step-card path (always the current cycle) and a current-cycle chip.
//   - FUTURE cycle   (any other occurrence) → `pendingStepSkips` (add/remove the
//     {occurrenceDate, stepIndex} override). This is the calendar chip path — the
//     menu appears on synthetic future occurrences, which WhatsAppSelectors hides
//     from `pendingStepSkips`, so touching `skippedSteps` there would do nothing.
//
// It dispatches the drip slice's `updateOne` by its ACTION TYPE STRING
// ("drips/updateOne"; the slice `name` is its service) rather than importing
// DBServiceThunks — that import would form a cycle (DBServiceReducers already
// imports THIS module). `EntityAdapter.updateOne` shallow-merges `changes`, so
// only the touched field + `version` move. The `version` bump keeps the store
// monotonic so a later same-version realtime echo is correctly ignored by the
// freshness guard; on failure both original arrays + version are restored exactly.
// The slice shape this optimistic path reads — narrowed structurally so we neither
// cast to `any` nor import the extension-layer RootState into common (which would
// invert the dependency direction). Only the fields actually touched are declared.
interface OptimisticDripEntity {
  version?: number;
  skippedSteps?: number[];
  pendingStepSkips?: PendingStepSkip[];
  schedule?: { dueDate?: string | number | Date | null };
}
interface DripSliceState {
  DBDripReducer?: { entities?: Record<string, OptimisticDripEntity | undefined> };
}

async function applyOptimisticStepSkip(
  api: { dispatch: (action: unknown) => void; getState: () => unknown; queryFulfilled: Promise<{ data: unknown }> },
  args: { dripId: string; occurrenceDate: string; stepIndex: number },
  isSkip: boolean,
): Promise<void> {
  const { dispatch, getState, queryFulfilled } = api;
  const { dripId, occurrenceDate, stepIndex } = args;
  const drip = (getState() as DripSliceState)?.DBDripReducer?.entities?.[dripId];
  if (!drip) {
    // No local drip to patch — still consume the promise so a rejection doesn't
    // surface as unhandled; the calling UI reflects failure on its own.
    try { await queryFulfilled; } catch { /* no local state to revert */ }
    return;
  }
  const baseVersion: number = typeof drip.version === "number" ? drip.version : 0;
  const originalSkipped: number[] = Array.isArray(drip.skippedSteps) ? [...drip.skippedSteps] : [];
  const originalPending: PendingStepSkip[] = Array.isArray(drip.pendingStepSkips) ? [...drip.pendingStepSkips] : [];

  const dueDate = drip?.schedule?.dueDate;
  const isCurrentCycle =
    dueDate != null &&
    Math.floor(new Date(dueDate).getTime() / 60000) === Math.floor(new Date(occurrenceDate).getTime() / 60000);

  const changes = isCurrentCycle
    ? {
        skippedSteps: isSkip
          ? addSkippedStep(originalSkipped, stepIndex)
          : removeSkippedStep(originalSkipped, stepIndex),
        version: baseVersion + 1,
      }
    : {
        pendingStepSkips: isSkip
          ? addPendingStepSkip(originalPending, occurrenceDate, stepIndex)
          : removePendingStepSkip(originalPending, occurrenceDate, stepIndex),
        version: baseVersion + 1,
      };

  const revert = () =>
    dispatch({
      type: "drips/updateOne",
      payload: { id: dripId, changes: { skippedSteps: originalSkipped, pendingStepSkips: originalPending, version: baseVersion } },
    });

  dispatch({ type: "drips/updateOne", payload: { id: dripId, changes } });
  try {
    // F2 — a NON-success status comes back HTTP 200 (the request was well-formed,
    // the server just declined to change anything): an un-skip of an already
    // advanced-past step answers `too-late`, and `not-recurring` / `not-found`
    // likewise write nothing. `queryFulfilled` still RESOLVES in those cases, so
    // the optimistic badge + version bump must be reverted here too — otherwise
    // the card reads "Scheduled" for a message that will never send (or drops a
    // "Skipped" badge that actually still holds). Only the true success status
    // (`skipped` for a skip, `unskipped` for an un-skip) keeps the write.
    const { data } = await queryFulfilled;
    const status = (data as { status?: string } | undefined)?.status;
    const successStatus = isSkip ? "skipped" : "unskipped";
    if (status !== successStatus) revert();
  } catch {
    // The server write outright FAILED (network / non-2xx) → restore the pre-op
    // arrays AND version exactly, so the store converges back to server truth
    // (unchanged) and no stale-ahead version blocks a later legitimate realtime
    // patch.
    revert();
  }
}

// Define a service using a base URL and expected endpoints
export const backendApi = createApi({
  reducerPath: "backendApi",
  tagTypes,
  keepUnusedDataFor: 1,
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    ...recycleApiBuilder(builder),
    ...chargebeeApiBuilder(builder),
    authenticate: builder.mutation<AuthResponse, AuthParams>({
      query: (authParams: AuthParams) => {
        try {
          console.log('[login-flow] backendApi.authenticate.query: building request', {
            strategy: authParams?.strategy,
            hasEmail: !!authParams?.email,
            hasPassword: !!authParams?.password,
            hasAccessToken: !!authParams?.accessToken,
            hasImpersonate: !!authParams?.impersonate,
          });
        } catch {

          console.log('[login-flow] backendApi.authenticate.query: error building request');
        }
        return {
          url: "authentication",
          method: "POST",
          body: authParams,
        };
      },
      async onQueryStarted(id, api) {
        await authenticateOnQueryStarted(id, api);
      },
    }),



    processDocument: builder.mutation<ProcessDocumentResponse, ProcessDocumentParams>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "knowledge-base.processDocument",
          ...params
        },
      }),
    }),
    validateChatId: builder.mutation<any, { chatId: string, options?: { useForceUsync?: boolean } }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "whatsapp-interface.validateChatId",
          ...params
        },
      }),
    }),
    chatbotTestInbound: builder.mutation<
      {
        sessionKey: string;
        replies: string[];
        diagnostics: {
          sendCount: number;
          latencyMs: number;
          turns?: {
            model: string;
            systemPromptUsed: string;
            historyUsed: { role: string; content: string }[];
            toolCalls: { name: string; inputPreview: string }[];
            latencyMs: number;
            contactMemoryBefore?: string;
            contactMemoryAfter?: string;
          }[];
        };
      },
      { botId: string; sessionKey: string; messages: string[] }
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "chatbot.test-inbound",
          ...params,
        },
      }),
    }),
    generateAIResponse: builder.mutation<GenerateAIResponseResult, GenerateAIResponseParams>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "generate-ai-response",
          ...params
        },
      }),
    }),
    register: builder.mutation<User, RegisterParams>({
      query: (registerParams: RegisterParams) => ({
        url: "users",
        method: "POST",
        body: {
          ...registerParams,
          cmd: "register"
        },
      }),
      async onQueryStarted(registerParams, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
          const res = await dispatch(
            authenticate.initiate({
              strategy: "local",
              email: registerParams.email,
              password: registerParams.password,
            })
          );
        } catch (err) {
          console.log(err);

          // Check if the error is due to email already existing (409 conflict)
          // if (err?.error?.status === 409 && 
          //     err?.error?.data?.message?.includes('email: value already exists')) {
          //   try {
          //     // Attempt to authenticate with the provided credentials
          //     const res = await dispatch(
          //       authenticate.initiate({
          //         strategy: "local",
          //         email: registerParams.email,
          //         password: registerParams.password,
          //       })
          //     );
          //     authenticateOnQueryStarted(undefined, { dispatch, queryFulfilled: res });
          //   } catch (authErr) {
          //     console.log('Authentication failed after email conflict:', authErr);
          //   }
          // }
        }
      },
    }),
    // The user doc as the server computes it now (users after.get adds `claudeAgentsActive`).
    // Light on purpose — unlike `authenticate`, no per-user reload follows.
    getUserById: builder.query<UserInterface, string>({
      query: (id) => ({ url: `users/${id}`, method: "GET" }),
      keepUnusedDataFor: 0,
    }),
    patchUser: builder.mutation<any, { id: string;[key: string]: any }>({
      query: ({ id, ...body }) => ({
        url: `users/${id}`,
        method: "PATCH",
        body,
      }),
    }),
    // `flow` marks which product flow asked for the reset ('clear' for the /clear-*
    // flow). It rides into the emailed link so the reset page can send the user back
    // to the right login screen — the link is opened from an email, so the flow can't
    // be recovered client-side.
    sendResetPassword: builder.mutation<{ state: string }, { email: string; flow?: string }>({
      query: ({ email, flow }) => ({
        url: "authManagement",
        method: "POST",
        body: {
          action: "sendResetPwd",
          value: flow ? { email, flow } : { email },
        },
      }),
    }),
    resetPassword: builder.mutation<any, { token: string; password: string }>({
      query: (params) => ({
        url: "authManagement",
        method: "POST",
        body: {
          action: "resetPwdLong",
          value: {
            ...params,
          },
        },
      }),
    }),

    changePassword: builder.mutation<any, { oldPassword: string; newPassword: string; }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "change-password",
          ...params,
        },
      }),
    }),
    passkeyStatus: builder.mutation<PasskeyStatusResult, void>({
      query: () => ({ url: "actions", method: "POST", body: { cmd: "passkeys.status" } }),
    }),
    // Public (no-auth) actions used by the login page.
    passkeySignInStatus: builder.mutation<PasskeySignInStatusResult, void>({
      query: () => ({ url: "actions", method: "POST", body: { cmd: "passkeys.signInStatus" } }),
    }),
    passkeySignInOptions: builder.mutation<PasskeySignInOptionsResult, void>({
      query: () => ({ url: "actions", method: "POST", body: { cmd: "passkeys.signInOptions" } }),
    }),
    passkeyList: builder.mutation<PasskeyListResult, void>({
      query: () => ({ url: "actions", method: "POST", body: { cmd: "passkeys.list" } }),
    }),
    passkeyRegisterOptions: builder.mutation<PasskeyOptionsResult, { password: string }>({
      query: (params) => ({ url: "actions", method: "POST", body: { cmd: "passkeys.registerOptions", ...params } }),
    }),
    passkeyRegisterVerify: builder.mutation<PasskeyVerifyResult, PasskeyRegisterVerifyParams>({
      query: (params) => ({ url: "actions", method: "POST", body: { cmd: "passkeys.registerVerify", ...params } }),
    }),
    passkeyRename: builder.mutation<PasskeyAckResult, { passkeyId: string; deviceName: string }>({
      query: (params) => ({ url: "actions", method: "POST", body: { cmd: "passkeys.rename", ...params } }),
    }),
    passkeyRemove: builder.mutation<PasskeyAckResult, { passkeyId: string; password: string }>({
      query: (params) => ({ url: "actions", method: "POST", body: { cmd: "passkeys.remove", ...params } }),
    }),
    getMediaTripState: builder.mutation<{ ok: boolean; tripped: boolean }, void>({
      query: () => ({ url: "actions?cmd=accountHealth.mediaTripState", method: "POST", body: { cmd: "accountHealth.mediaTripState" } }),
    }),
    requestMediaReload: builder.mutation<{ ok: boolean; outcome?: string }, void>({
      query: () => ({ url: "actions?cmd=accountHealth.requestMediaReload", method: "POST", body: { cmd: "accountHealth.requestMediaReload" } }),
    }),
    getSettings: builder.mutation<Settings, void>({
      query: (params) => ({
        url: "actions?cmd=get-settings",
        method: "POST",
        body: {
          cmd: "get-settings",
        },
      }),
    }),
    getDashboardMessages: builder.mutation<GetDashboardMessagesResponse, GetDashboardMessagesParams>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "get-dashboard-messages",
          ...params
        },
      }),
    }),
    acknowledgeMessage: builder.mutation<{ success: boolean; error?: string }, { messageId: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "acknowledge-message",
          ...params
        },
      }),
    }),
    generateUploadUrl: builder.mutation<GenerateUploadUrlResponse, string>({
      transformResponse: (response: any, meta, mediaId) => {
        return {
          ...response,
          mediaId,
        };
      },
      query: () => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "generate-upload-url",
        },
      }),
    }),
    cancelSubscription: builder.mutation<
      Subscription,
      { subscriptionID: string; reason: string }
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "cancelSubscription",
          subscriptionID: params.subscriptionID,
          reason: params.reason,
        },
      }),
    }),
    addUserToTaskGroup: builder.mutation<any, string>({
      query: (shareId) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "add-participant-task-group",
          shareId,
        },
      }),
    }),
    canISend: builder.mutation<{ wasSendAttempted: boolean, creationDate: string }, string>({
      query: (messageId) => ({
        url: "actions?cmd=can-i-send",
        method: "POST",
        body: {
          messageId,
        },
      }),
    }),
    addNewBoardMemberApi: builder.mutation<any, string>({
      query: (newMemberEmail) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "add-new-board-member",
          newMemberEmail,
        },
      }),
    }),
    nextCampaignMessage: builder.mutation<NextCampaignMessageResponse, string>({
      query: (campaignId: string) => {
        // console.log('call for next-campaign-message cmd')
        return {
          url: "actions?cmd=next-campaign-message",
          method: "POST",
          body: { campaignId },
        };
      },
    }),
    getInProgressCampaigns: builder.mutation<Campaign[], void>({
      query: () => {
        return {
          url: "actions?cmd=get-in-progress-campaigns",
          method: "POST",
        };
      },
    }),

    getScheduledMessagesInRedis: builder.query<any, string>({
      query: (campaignId) => ({
        url: "actions?cmd=get-scheduled-messages-in-redis",
        method: "POST",
        body: {
          campaignId
        },
      }),
    }),



    waMessageListenerEvent: builder.mutation<void, WaListenerEventEnvelope>({
      // rev 3.3 §6.5 (F3): bounded retry on HTTP 503 ONLY — the backend's agent
      // ingress throws 503 before any side effect, so re-delivery is safe. All
      // other statuses behave exactly as before (single attempt).
      queryFn: async (envelope, _api, _extraOptions, baseQuery) =>
        (await runWithRetryOn503(() =>
          baseQuery({
            url: "actions?cmd=wa-message-listener-event",
            method: "POST",
            body: envelope,
          }),
        )) as any,
    }),

    duplicateAudienceBoardForCampaigns: builder.mutation<duplicateAudienceBoardForCampaignsResponse, string>({
      query: (oldBoardId: string) => {
        return {
          url: "actions",
          method: "POST",
          body: { cmd: "duplicate-audience-board-for-campaigns", oldBoardId },
        };
      },
    }),
    duplicateTemplateForCampaigns: builder.mutation<duplicateTemplateForCampaignsResponse, string>({
      query: (oldTemplateId: string) => {
        return {
          url: "actions",
          method: "POST",
          body: { cmd: "duplicate-template-for-campaigns", oldTemplateId },
        };
      },
    }),
    removeAllPendingUserMessagesByCampaignId: builder.mutation<any, string>({
      query: (campaignId: string) => {
        return {
          url: "actions",
          method: "POST",
          body: { cmd: "remove-all-pending-user-messages-by-campaignId", campaignId },
        };
      },
    }),
    resumeCampaign: builder.mutation<NextCampaignMessageResponse, string>({
      query: (campaignId: string) => {
        return {
          url: "actions",
          method: "POST",
          body: { cmd: "resume-campaign", campaignId },
        };
      },
    }),
    deleteCampaign: builder.mutation<Campaign, string>({
      query: (campaignId: string) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "delete-campaign", campaignId },
      }),
    }),
    // Engine reply-exit: on an inbound message, the WA communication layer
    // reports the sender's identity equivalents so the backend can exit that
    // contact from any active stopOnReply drip. `reply` carries a best-effort
    // snapshot of the triggering message (text/media) so the UI can show WHAT
    // the recipient replied. Fire-and-forget from onMessage.
    registerDripReply: builder.mutation<
      { exitedFrom: number },
      { whatsappIds: string[]; reply?: { text?: string; mediaType?: string; thumbnail?: string } }
    >({
      query: ({ whatsappIds, reply }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "drip.register-reply", whatsappIds, reply },
      }),
    }),
    // Catch-up companion to registerDripReply: on engine reconnect the content
    // script asks the backend for the owner's active stop-on-reply drip targets
    // (recipient whatsappId + the epoch-ms instant from which an inbound counts
    // as a reply) so it can scan the WA store for replies that landed while the
    // engine was offline. Read-only; the actual exit still goes through
    // registerDripReply.
    getActiveDripReplyTargets: builder.query<{ whatsappId: string; sinceTs: number }[], void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "drip.active-reply-targets" },
      }),
    }),
    // Drip lifecycle actions — mirror resumeCampaign/deleteCampaign: an
    // `actions` POST with a cmd + the drip id. The orchestrator launches the
    // first step's campaign (launch), stops scheduling further steps (pause),
    // or tears the whole sequence down (abort). All return the updated Drip;
    // the realtime MQTT patch keeps the T5 drips slice in sync.
    launchDrip: builder.mutation<Drip, string>({
      query: (dripId: string) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "launch-drip", dripId },
      }),
    }),
    pauseDrip: builder.mutation<Drip, string>({
      query: (dripId: string) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "pause-drip", dripId },
      }),
    }),
    // Resume = set active AND catch up a step that completed while paused (the
    // completion hook's advance was skipped). See resumeDrip on the backend.
    resumeDrip: builder.mutation<{ status: string }, string>({
      query: (dripId: string) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "resume-drip", dripId },
      }),
    }),
    abortDrip: builder.mutation<Drip, string>({
      query: (dripId: string) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "abort-drip", dripId },
      }),
    }),
    // #590: skip the NEXT occurrence of a recurring drip — cancels the imminent
    // (pending) step campaign and rolls the recurrence forward by exactly one
    // occurrence, without touching the drip's paused/active status. The backend
    // action (`skipNextOccurrenceAction`) is idempotent, so a double-tap does
    // not launch two successors. Recurring drips only; the menu item gates on
    // `schedule.isRecurring` before it is ever offered.
    skipNextOccurrence: builder.mutation<{ status: string }, string>({
      query: (dripId: string) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "skip-next-occurrence", dripId },
      }),
    }),
    // #592b: skip ONE specific date of a recurring drip (imminent OR far-future)
    // without stopping the series. The backend writes an EXDATE onto the drip's
    // schedule (idempotent), the calendar hides that occurrence, and the series
    // advances past it. `dueDate` is the OCCURRENCE START instant (the rrule
    // instant the chip carries as `dripOccurrenceStartMs`).
    skipDripOccurrence: builder.mutation<{ status: string }, { dripId: string; dueDate: string }>({
      query: ({ dripId, dueDate }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "skip-drip-occurrence", dripId, dueDate },
      }),
    }),
    // #592c: skip ONE step ("message") of a recurring drip's cycle for a chosen
    // occurrence, applied to ALL contacts, without stopping the cycle or the
    // series. For the CURRENT cycle the backend records the skip on the drip
    // (aborting the step's campaign if it is the pending one and advancing past
    // it); for a not-yet-materialized cycle it stashes a pending override
    // consumed at the matching roll. Idempotent (`$addToSet`) and BE#490-race
    // safe. `occurrenceDate` is the OCCURRENCE START instant (the rrule instant
    // the chip carries as `dripOccurrenceStartMs`); `stepIndex` is the chip's
    // own `dripStepIndex`.
    skipDripStep: builder.mutation<{ status: string }, { dripId: string; occurrenceDate: string; stepIndex: number }>({
      query: ({ dripId, occurrenceDate, stepIndex }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "skip-drip-step", dripId, occurrenceDate, stepIndex },
      }),
      // #592c — flip the step to "Skipped" instantly (card + calendar), no reload.
      async onQueryStarted({ dripId, occurrenceDate, stepIndex }, { dispatch, getState, queryFulfilled }) {
        await applyOptimisticStepSkip({ dispatch, getState, queryFulfilled }, { dripId, occurrenceDate, stepIndex }, true);
      },
    }),
    // #592c: UNDO a previously-recorded single-step skip, for a step whose send
    // time has NOT yet passed. Mirrors `skipDripStep` exactly (same
    // `{dripId, occurrenceDate, stepIndex}` shape) and removes the skip override
    // the backend stashed. Returns `unskipped` on success, or `not-recurring` /
    // `not-found` / `too-late` (the step already fired) so the client can react.
    unskipDripStep: builder.mutation<{ status: string }, { dripId: string; occurrenceDate: string; stepIndex: number }>({
      query: ({ dripId, occurrenceDate, stepIndex }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "unskip-drip-step", dripId, occurrenceDate, stepIndex },
      }),
      // #592c — clear the "Skipped" badge instantly (card + calendar), no reload.
      async onQueryStarted({ dripId, occurrenceDate, stepIndex }, { dispatch, getState, queryFulfilled }) {
        await applyOptimisticStepSkip({ dispatch, getState, queryFulfilled }, { dripId, occurrenceDate, stepIndex }, false);
      },
    }),
    // #103 tier 2 — editing a campaign after launch. Three actions, each ONE
    // server call the client cannot half-apply. The template patch, the drip
    // step mirror, the campaign/drip resume and the NON-idempotent `resumeDrip`
    // catch-up are all sequenced server-side, in order, awaited; the client
    // renders the definitive outcome it gets back and orchestrates nothing.
    // (An earlier client-side version of this could double-send a whole
    // audience — see campaign-edit-actions.ts on the backend.)
    //
    // No `invalidatesTags` and no refetch is needed: every transition these
    // perform is published over MQTT (campaign-edit-actions.publishEntity), so
    // the dbService slices update themselves.
    beginCampaignEdit: builder.mutation<CampaignEditBeginResult, CampaignEditBeginRequest>({
      query: ({ campaignId }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "campaign-edit-begin", campaignId },
      }),
    }),
    commitCampaignEdit: builder.mutation<CampaignEditFinishResult, CampaignEditCommitRequest>({
      query: ({ campaignId, template, editSessionId }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "campaign-edit-commit", campaignId, template, editSessionId },
      }),
    }),
    // `recover: true` is the explicit "Resume sending" affordance and NOTHING
    // else — the cancel/unmount path sends only `editSessionId`. The backend
    // deliberately distinguishes the two: an abort that merely LOST its session
    // id is not a recovery mandate.
    abortCampaignEdit: builder.mutation<CampaignEditFinishResult, CampaignEditAbortRequest>({
      query: ({ campaignId, editSessionId, recover }) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "campaign-edit-abort",
          campaignId,
          editSessionId,
          ...(recover ? { recover: true } : {}),
        },
      }),
    }),
    // #103 tier 1 (standalone): edit a campaign that has NOT started sending —
    // message, audience and send time together, in ONE atomic write guarded on
    // the campaign still being `pending`. Deliberately not part of the
    // begin/commit/abort cycle above: there is no pause here, because nothing is
    // dispatching.
    //
    // Only the fields the user actually changed are sent; an omitted field means
    // "leave it alone", which is why they are spread conditionally rather than
    // passed as `undefined` (JSON drops an undefined key, but an explicit `null`
    // would not, and the backend distinguishes absent from empty).
    editPendingCampaign: builder.mutation<PendingCampaignEditResult, PendingCampaignEditRequest>({
      query: ({ campaignId, template, boardIDArray, dueDate }) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "campaign-edit-pending",
          campaignId,
          ...(template ? { template } : {}),
          ...(boardIDArray ? { boardIDArray } : {}),
          ...(dueDate ? { dueDate } : {}),
        },
      }),
    }),
    orderDetails: builder.mutation<Invoice[], string>({
      query: (subscriptionId: string) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "order-details", subscriptionId },
      }),
    }),
    getUserPaymentParams: builder.mutation<
      UserPaymentParams,
      GetUserPaymentParamsRequest
    >({
      query: ({ productId }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "user-payment-params", productId },
      }),
    }),
    getPlanMap: builder.mutation<Record<string, PlanDescriptor>, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "plan-map" },
      }),
    }),
    changePlan: builder.mutation<
      { changeSuccessful: boolean },
      { contractId: string }
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "change-plan", ...params },
      }),
    }),

    completePurchase: builder.mutation<
      { changeSuccessful: boolean, message?: string },
      ShoppingContextParams
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "complete-purchase", ...params },
      }),
      transformResponse: (response, meta) => {
        const hasError = !!(response as any).error || !!(response as any).message;
        const [errorMessage] = (response as any).message ?? [];
        return { changeSuccessful: !hasError, message: errorMessage?.description };
      }
    }),
    calculateCart: builder.mutation<BluesnapCartInfo & BluesnapErrorOutput & CalculateCartParams, CalculateCartParams>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "get-cart-price", ...params },
      }),
      transformResponse: (response: any, meta, params) => {
        return {
          ...response,
          ...params
        };
      }
    }),
    getWorkspaceInvoices: builder.mutation<WorkspaceInvoices, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "get-workspace-invoices" },
      }),
    }),
    getInvoice: builder.mutation<BluesnapInvoice, { invoiceId: string }>({
      query: ({ invoiceId }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "get-invoice", invoiceId },
      }),
    }),
    getShopperInfo: builder.mutation<{
      planName: string,
      shopper: BluesnapShopper;
      customer: Customer;
      subscription: BluesnapSubscription;
      paymentSourceList: PaymentSource[];
      manage_payment_sources_url?: string;
    }, void | { subscriptionId?: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "get-shopper-info", ...(params && { subscriptionId: params.subscriptionId }) },
      }),
      transformResponse: (response: any) => {
        return response?.error ? undefined : response;
      },
    }),
    getPaypalPaymentUrl: builder.mutation<BluesnapShoppingContext & BluesnapErrorOutput, ShoppingContextParams>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "get-paypal-payment-url", ...params },
      }),
      transformResponse: (response: any, meta, params) => {
        return {
          ...response,
          ...params
        };
      }
    }),
    getCheckoutPageUrl: builder.mutation<GetCheckoutPageUrlResponse, GetCheckoutPageUrlParams>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.getCheckoutPage", ...params },
      })
    }),
    cancelActiveSubscription: builder.mutation<void, { cancellation?: CancellationSurveyPayload } | void>({
      query: (arg) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.cancelActiveSubscription", ...(arg && 'cancellation' in arg ? { cancellation: arg.cancellation } : {}) },
      }),
    }),
    // #241 — the cancel dialog's server-driven switches (tf config-map flags).
    getCancelFlowConfig: builder.query<CancelFlowConfig, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.getCancelFlowConfig" },
      }),
    }),
    // EU withdrawal function (Dir. 2011/83 Art 11a): prefill + contracts, then the submission.
    getWithdrawalContext: builder.query<WithdrawalContext, void>({
      query: () => ({ url: "actions", method: "POST", body: { cmd: "withdrawal.getContext" } }),
    }),
    submitWithdrawal: builder.mutation<WithdrawalResult, WithdrawalSubmitInput>({
      query: (input) => ({ url: "actions", method: "POST", body: { cmd: "withdrawal.submit", ...input } }),
    }),
    // #241 — accept the annual save: the server mints the 15% yearly coupon + a hosted checkout.
    startAnnualSaveCheckout: builder.mutation<{ checkoutPageUrl: string; quote: AnnualSaveQuote }, { reasonCode?: string; reasonDetail?: string; source?: 'cancel_flow' | 'renewal_email' }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.startAnnualSaveCheckout", ...params },
      }),
    }),
    // BE#498 — pause save offer (Chargebee native pause, auto-resume in N months).
    pauseSubscription: builder.mutation<{ success: boolean; paused: number; failed: number; resumeInMonths: number }, { resumeInMonths?: number } | void>({
      query: (arg) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.pauseSubscription", ...(arg && 'resumeInMonths' in arg ? { resumeInMonths: arg.resumeInMonths } : {}) },
      }),
    }),
    // BE#498 — discount save offer (capped-40% SAVE_ coupon applied to active subs).
    applySaveOfferDiscount: builder.mutation<{ success: boolean; couponId: string; discountPercentage: number; applied: number; failed: number }, { discountPct: number; durationMonths?: number; reasonCode?: string; reasonDetail?: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.applySaveOfferDiscount", ...params },
      }),
    }),
    // BE#498 — durable record of a cancel-flow DISMISS ("Keep my subscription" / reconsidered):
    // a flow SUCCESS. Needed to compute retention_rate = (saved + kept) / decided.
    recordCancelFlowDismiss: builder.mutation<{ recorded: boolean }, { reasonCode?: string; reasonDetail?: string; offerShown?: string; step?: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.recordCancelFlowDismiss", ...params },
      }),
    }),
    // Refer-a-friend (#186): the owner's code, share link, offer and counts. `{ enabled: false }`
    // while the program is off; the backend rejects users who own no workspace.
    getMyReferral: builder.query<ReferralSummary, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "referral.getMine" },
      }),
    }),
    getPortalSessionUrl: builder.mutation<{
      portal_session_url: string;
    }, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.getPortalSessionUrl" },
      }),
    }),
    updateShopperInfo: builder.mutation<void, { shopperInfo: ShopperInfo }>({
      query: ({ shopperInfo }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "update-shopper-info", shopperInfo },
      }),
    }),
    getEmailWorkspaces: builder.mutation<void, { email: string }>({
      query: ({ email }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "get-user-workspaces", email },
      }),
    }),
    updateSubscriptionWorkspace: builder.mutation<void, { workspaceId: string, subscriptionId: string }>({
      query: ({ workspaceId, subscriptionId }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "update-subscription-workspace", workspaceId, subscriptionId },
      }),
    }),

    /* getSignedUploadUrl changed from pull request of team inbox https://bitbucket.org/noammazuz/whatsapp-scheduler/pull-requests/133 */
    getSignedUploadUrl: builder.mutation<{ url: string; publicUrl: string }, string | { contentType: string; expiresInMinutes?: number }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "signed-upload-url",
          ...(typeof params === "string" ? { contentType: params } : params),
        },
      }),
    }),

    getEntity: builder.query<any, GetParams>({
      query: ({ service, _id }) => ({
        url: `${service}/${_id}`,
        method: "GET",
      }),
    }),
    findEntity: builder.query<Paginated<any>, FindParams>({
      query: ({ service, query }) => {
        let url = service;
        if (query) {
          url += `/?${qs.stringify(query)}`;
        }
        return {
          url,
          method: "GET",
        };
      },
    }),

    // AWS SES EMAIL TESTING
    // send awsEmail
    sendAwsEmail: builder.mutation<void, { email: string, templateId: string, title: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "send-aws-email-to-email", ...params },
      }),
    }),

    // send aws email to subscriber
    sendAwsEmailToSubscriber: builder.mutation<void, { email: string, templateId: string, title: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "send-aws-email-to-subscriber", ...params },
      }),
    }),
    sendEmailFromClient: builder.mutation<void, { userDeletedTitle: string, userDeletedEmail: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "send-email-from-client", ...params },
      }),
    }),


    // unsubscribe from listmonk
    unsubscribeFromListmonk: builder.mutation<void, { subscriptionID: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "unsubscribe-from-listmonk", ...params },
      }),
    }),

    getTotalCount: builder.query<number, { service: string }>({
      query: ({ service }) => {
        let url = service;
        const query = { $limit: 0 }; // Only fetch the total count
        url += `/?${qs.stringify(query)}`;
        return {
          url,
          method: "GET",
        };
      },
      transformResponse: (response: { total: number }) => response.total,
    }),
    createEntity: builder.mutation<any, CreateParams>({
      query: ({ service, entity }) => ({
        // Surface the action cmd in the URL (e.g. actions?cmd=sendWebhookEvent)
        // so requests are filterable in the network tab. The cmd still rides
        // in the body too; this only affects entities that carry a cmd.
        url: entity?.cmd
          ? `${service}?cmd=${encodeURIComponent(entity.cmd)}`
          : `${service}`,
        method: "POST",
        body: entity,
      }),
    }),

    patchEntity: builder.mutation<any, CreateParams>({
      query: ({ service, entity }) => ({
        url: `${service}/${entity._id}`,
        method: "PATCH",
        body: entity,
      }),
    }),
    deleteEntity: builder.mutation<any, CreateParams>({
      query: ({ service, entity }) => ({
        url: `${service}/${entity._id}`,
        method: "DELETE",
        body: entity,
      }),
    }),
    bulkDeleteEntities: builder.mutation<any[], BulkDeleteParams>({
      query: ({ service, query }) => ({
        url: `${service}?${qs.stringify(query)}`,
        method: "DELETE",
      }),
    }),
    updateManyEntities: builder.mutation<
      UpdateManyResponse<any>,
      CreateManyParams
    >({
      query: ({ service, entities }) => ({
        url: "actions?cmd=update-many-entities",
        method: "POST",
        body: {
          cmd: "update-many-entities",
          service,
          entities,
        },
      }),
    }),
    createManyEntities: builder.mutation<
      CreateManyResponse<any>,
      CreateManyParams
    >({
      query: ({ service, entities }) => ({
        url: "actions?cmd=create-many-entities",
        method: "POST",
        body: {
          cmd: "create-many-entities",
          service,
          entities,
        },
      }),
    }),



    addExistingWorkspaceUserToBoard: builder.mutation<
      any,
      {
        boardId: string;
        userId: string;
        role: string;
      }
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "add-existing-workspace-user-to-board",
          ...params
        },
      }),
    }),
    addNonExistingWorkspaceUserToBoard: builder.mutation<
      any,
      {
        boardId: string;
        email: string;
        role: string;
      }
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "add-non-existing-workspace-user-to-board",
          boardId: params.boardId,
          email: params.email,
          role: params.role,
        },
      }),
    }),

    resendBoardInvitation: builder.mutation<
      any,
      {
        boardId: string;
        boardUserId: string;
        email: string;
      }
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "resend-board-invitation",
          boardId: params.boardId,
          boardUserId: params.boardUserId,
          email: params.email,

        },
      }),
    }),
    //   Send a Massage code
    sendAddToWorkspace: builder.mutation<void, string>({
      query: (email: string) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "send-add-to-workspace",
          value: { email },
        },
      }),
    }),
    importExcelToBoard: builder.mutation<void, ImportExcelToBoardOptions>({
      query: (options: ImportExcelToBoardOptions) => {
        const formData = new FormData();

        formData.append('cmd', 'import-excel')
        formData.append('boardId', options.boardId)
        formData.append('selectedColumn', options.selectedColumn)
        formData.append('importDataMapping', JSON.stringify(options.importDataMapping))
        formData.append('sheetName', JSON.stringify(options.sheetName))
        formData.append('importFileName', JSON.stringify(options.importFileName))
        formData.append('file', options.file)

        return {
          url: "actions",
          method: "POST",
          body: formData,
        }
      },
    }),
    initialStep: builder.mutation<TransitionData, { cmd: string; event: string }>({
      query: (body) => ({
        url: '/analytics',
        method: 'POST',
        body,
      }),
    }),
    getNextStep: builder.mutation<TransitionData, { cmd: string; sessionId: string; event: string }>({
      query: (body) => ({
        url: '/analytics',
        method: 'POST',
        body,
      }),
    }),
    acceptWorkspaceInvention: builder.mutation<
      AcceptInventionResponse,
      AcceptWorkspaceInvitationParams
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "accept-workspace-invention",
          ...params
        },
      }),
    }),
    acceptBoardInvention: builder.mutation<
      AcceptInventionResponse,
      AcceptBoardInvitationParams
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "accept-board-invention",
          ...params,
          boardId: params.boardId,
        },
      }),
    }),
    getLinkPreview: builder.mutation<LinkPreview, { link: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "link-preview",
          ...params,
        },
      }),
    }),
    createAndDeleteAutomation: builder.mutation<void, any>({
      query: (options: any) => {
        ;
        const formData = new FormData();
        formData.append('cmd', 'automation')
        formData.append('automationDetails', JSON.stringify(options.combined))
        formData.append('automationId', JSON.stringify(options.automationId))
        return {
          url: "actions",
          method: "POST",
          body: formData,
        }
      },
    }),
    checkSession: builder.mutation<Settings, void>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "check-session",
        },
      }),
    }),
    createUserSession: builder.mutation<Settings, void>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "create-session",
        },
      }),
    }),
    duplicateGroup: builder.mutation<any, { groupId: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "duplicate-group",
          ...params,
        },
      }),
    }),

    generateApiKey: builder.mutation<
      Partial<User>,
      Partial<User>
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "generate-api-key",
          ...params
        },
      }),
    }),
    listPausedSeries: builder.query<PausedSeriesEntry[], void>({
      query: () => ({ url: "actions", method: "POST", body: { cmd: "list-paused-series" } }),
      providesTags: ['PausedSeries'],
    }),
    resumePausedSeries: builder.mutation<ResumePausedSeriesResult, { messageId: string }>({
      query: ({ messageId }) => ({ url: "actions", method: "POST", body: { cmd: "resume-paused-series", messageId } }),
      invalidatesTags: ['PausedSeries', 'user-messages'],
    }),

    listApiKeys: builder.query<ApiKeyRow[], { workspaceId: string }>({
      query: ({ workspaceId }) => ({
        url: `internal/api-keys?workspaceId=${encodeURIComponent(workspaceId)}`,
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        if (Array.isArray(response)) return response;
        return response?.data ?? [];
      },
      providesTags: ['ApiKeys'],
    }),

    createApiKey: builder.mutation<
      ApiKeyCreateResponse,
      { workspaceId: string; name: string; scopes?: string[] }
    >({
      query: ({ workspaceId, name, scopes }) => ({
        url: 'internal/api-keys',
        method: 'POST',
        body: { workspaceId, name, ...(scopes ? { scopes } : {}) },
      }),
      invalidatesTags: ['ApiKeys'],
    }),

    renameApiKey: builder.mutation<ApiKeyRow, { id: string; name: string }>({
      query: ({ id, name }) => ({
        url: `internal/api-keys/${id}`,
        method: 'PATCH',
        body: { name },
      }),
      invalidatesTags: ['ApiKeys'],
    }),

    revokeApiKey: builder.mutation<ApiKeyRow, { id: string }>({
      query: ({ id }) => ({
        url: `internal/api-keys/${id}`,
        method: 'PATCH',
        body: { revokedAt: new Date().toISOString() },
      }),
      invalidatesTags: ['ApiKeys'],
    }),

    // ── My Bridge endpoints ────────────────────────────────────────────────
    // Bridge + agents overview for the logged-in user (Feathers find → GET).
    getMyBridgeOverview: builder.query<MyBridgeOverview, void>({
      query: () => ({ url: 'my-bridge/overview', method: 'GET' }),
      providesTags: ['MyBridge'],
      // The WA tab forwards incoming messages to the agents backend only when the
      // user doc says `claudeAgentsActive` — loaded at login, so a user's FIRST
      // enabled agent was never fed messages until a reload. Every agent change
      // refetches this overview (tag 'MyBridge'); re-read the user when they disagree.
      async onQueryStarted(_arg, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const hasEnabled = (data?.agents ?? []).some((a) => a.enabled);
          const flagged = (getState() as { UserReducer?: { user?: { claudeAgentsActive?: boolean } } }).UserReducer?.user?.claudeAgentsActive === true;
          if (hasEnabled && !flagged) void dispatch(refreshClaudeAgentsActiveThunk());
        } catch { /* a failed overview / refresh changes nothing */ }
      },
    }),
    // The account's chats, for the agent form's pickers. The endpoint still
    // requires a clientId on current backends → the implicit legacy 'default'.
    getMyBridgeChats: builder.query<MyBridgeChat[], void>({
      query: () => ({ url: withQuery('my-bridge/chats', { clientId: MY_BRIDGE_LEGACY_CLIENT_ID }), method: 'GET' }),
      transformResponse: (response: any) => (Array.isArray(response) ? response : response?.data ?? []),
    }),
    createMyBridgeAgent: builder.mutation<MyBridgeAgent, CreateMyBridgeAgentParams>({
      query: (body) => ({ url: 'my-bridge/agents', method: 'POST', body }),
      invalidatesTags: ['MyBridge'],
    }),
    // ── async build (polling) — start a job, then poll it ─────────────────────
    startMyBridgeAgentBuild: builder.mutation<{ jobId: string; status: string }, BuildMyBridgeAgentParams>({
      query: (body) => ({ url: 'my-bridge/agents/build/jobs', method: 'POST', body }),
    }),
    getMyBridgeAgentBuildJob: builder.query<MyBridgeBuildJob, string>({
      query: (jobId) => ({ url: `my-bridge/agents/build/jobs/${encodeURIComponent(jobId)}`, method: 'GET' }),
    }),
    // Stop a running build/edit job (the chat's Stop button, create or edit alike).
    // Idempotent: a job no longer running answers 200 with its current status. The one
    // edge case — `status: 'running'` — means the input had not landed on the session yet
    // (within ms of the start); the caller should retry the stop once, shortly after.
    stopMyBridgeAgentBuildJob: builder.mutation<{ jobId: string; status: 'running' | 'done' | 'error' | 'stopped' }, { jobId: string }>({
      query: ({ jobId }) => ({ url: `my-bridge/agents/build/jobs/${encodeURIComponent(jobId)}/stop`, method: 'POST' }),
    }),
    // ── Edit Agent chat (work on an existing agent with the builder) ───────────
    getMyBridgeAgentBuilderChat: builder.query<MyBridgeAgentBuilderChat, { agentId: string }>({
      query: ({ agentId }) => ({ url: myBridgeAgentUrl(agentId, 'builder-chat'), method: 'GET' }),
    }),
    // F6: no `conversationId` = a NEW conversation; follow-ups of one open chat send back the
    // `conversationId` the previous turn returned (honoured only while it is the agent's current one).
    sendMyBridgeAgentBuilderChat: builder.mutation<
      { jobId: string; status: string; conversationId?: string },
      { agentId: string; prompt: string; attachments?: string[]; conversationId?: string }
    >({
      query: ({ agentId, prompt, attachments, conversationId }) => ({
        url: myBridgeAgentUrl(agentId, 'builder-chat'),
        method: 'POST',
        body: { prompt, attachments, ...(conversationId ? { conversationId } : {}) },
      }),
    }),
    updateMyBridgeAgent: builder.mutation<MyBridgeAgent, UpdateMyBridgeAgentParams>({
      query: ({ id, ...body }) => ({ url: myBridgeAgentUrl(id), method: 'PATCH', body }),
      invalidatesTags: ['MyBridge'],
    }),
    deleteMyBridgeAgent: builder.mutation<{ ok: boolean }, { id: string }>({
      query: ({ id }) => ({ url: myBridgeAgentUrl(id), method: 'DELETE' }),
      invalidatesTags: ['MyBridge'],
    }),

    // ── Agent policy (deterministic capability contract: incoming/send/tools) ──
    setMyBridgeAgentPolicy: builder.mutation<
      MyBridgeAgentPolicy,
      {
        agentId: string;
        incomingEnabled?: boolean;
        toolsWhatsappMcp?: boolean;
        /** May answer with a voice note (the agent's `voice` tool). */
        toolsVoice?: boolean;
        /** When to answer with a voice note instead of text ('' = the default rule). */
        voiceWhen?: string;
        /** The voice it speaks with ('' = the default). */
        voiceName?: string;
        /** Manages its own Blueticks boards. */
        toolsBoards?: boolean;
        /** "Use an existing board for <kind>": the board becomes the agent's board of that kind. */
        adoptBoards?: { boardId: string; kind: string }[];
        /** Answers WhatsApp calls with a live voice conversation (the agent's `calls`). */
        toolsCalls?: boolean;
        toolsSuno?: boolean;
        toolsImages?: boolean;
        read?: MyBridgeReadPolicyInput | null;
      }
    >({
      query: (body) => ({ url: 'my-bridge/agent-policy', method: 'POST', body }),
      invalidatesTags: ['MyBridge'],
    }),

    // ── Fresh per-agent reads for the Edit dialog (avoid stale overview) ───────
    getMyBridgeVoices: builder.query<MyBridgeVoiceCatalog, void>({
      query: () => ({ url: 'my-bridge/voices', method: 'GET' }),
    }),
    /** A short sample of a voice in the UI language (OGG/Opus, base64). */
    previewMyBridgeVoice: builder.mutation<{ mime_type: string; data_base64: string }, { voice: string; lang: string }>({
      query: (body) => ({ url: 'my-bridge/voice-preview', method: 'POST', body }),
    }),
    getMyBridgeAgentPolicy: builder.query<MyBridgeAgentPolicy, { agentId: string }>({
      query: ({ agentId }) => ({ url: withQuery('my-bridge/agent-policy', { agentId }), method: 'GET' }),
      providesTags: ['MyBridge'],
    }),
    getMyBridgeAgentSchedules: builder.query<MyBridgeSchedule[], { agentId: string }>({
      query: ({ agentId }) => ({ url: withQuery('my-bridge/agent-schedules', { agentId }), method: 'GET' }),
      providesTags: ['MyBridge'],
    }),

    // ── Agent time-triggers (schedules) ────────────────────────────────────────
    // Current backends still require a clientId on create → the implicit legacy 'default'.
    createMyBridgeSchedule: builder.mutation<MyBridgeSchedule, MyBridgeScheduleInput & { agentId: string }>({
      query: (body) => ({ url: 'my-bridge/agent-schedules', method: 'POST', body: { ...body, clientId: MY_BRIDGE_LEGACY_CLIENT_ID } }),
      invalidatesTags: ['MyBridge'],
    }),
    updateMyBridgeSchedule: builder.mutation<MyBridgeSchedule, { id: string } & Partial<MyBridgeScheduleInput>>({
      query: ({ id, ...body }) => ({ url: `my-bridge/agent-schedules/${encodeURIComponent(id)}`, method: 'PATCH', body }),
      invalidatesTags: ['MyBridge'],
    }),
    deleteMyBridgeSchedule: builder.mutation<{ id: string; deleted: boolean }, { id: string }>({
      query: ({ id }) => ({ url: `my-bridge/agent-schedules/${encodeURIComponent(id)}`, method: 'DELETE' }),
      invalidatesTags: ['MyBridge'],
    }),
    // "Run now" — fire one schedule immediately via the real activation path.
    runMyBridgeScheduleNow: builder.mutation<
      { ok: boolean; reply?: string; pending?: boolean; note?: string; deliveredTo?: string },
      { scheduleId: string }
    >({
      query: (body) => ({ url: 'my-bridge/agent-schedule-run', method: 'POST', body }),
    }),

    // ── Bridge Inspector reads (admin transparency into agent sessions) ────────
    getMyBridgeInspectBridge: builder.query<MyBridgeInspectBridge, { bridgeId?: string }>({
      query: ({ bridgeId }) => ({ url: withQuery('my-bridge/inspect-bridge', { bridgeId }), method: 'GET' }),
    }),
    getMyBridgeAgentChatSessions: builder.query<MyBridgeAgentChatSessions, { agentId: string; bridgeId?: string }>({
      query: ({ agentId, bridgeId }) => ({ url: myBridgeAgentUrl(agentId, 'chat-sessions', { bridgeId }), method: 'GET' }),
      providesTags: ['MyBridgeSessions'],
    }),
    // rev 3.3 per-conversation takeover (agent_conversations). Owner-scoped on the backend.
    getMyBridgeAgentTakeoverConversations: builder.query<MyBridgeAgentConversationsPage, { agentId: string; limit?: number; before?: string }>({
      query: ({ agentId, limit, before }) => ({ url: myBridgeAgentUrl(agentId, 'agent-conversations', { limit, before }), method: 'GET' }),
      providesTags: ['MyBridgeSessions'],
    }),
    // THE takeover action — the inspector's Sessions row and the conversations
    // panel both use it (the live `status` and the conversation's own flags).
    // `bridgeId`: an operator acting on another owner's bridge (the /bridges inspector).
    setMyBridgeConversationTakeover: builder.mutation<unknown, { agentId: string; chatId: string; bridgeId?: string } & MyBridgeConversationTakeoverPatch>({
      query: ({ agentId, chatId, bridgeId, ...body }) => ({
        url: myBridgeAgentUrl(agentId, `conversations/${encodeURIComponent(chatId)}/takeover`, { bridgeId }),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['MyBridgeSessions'],
    }),
    // "Delete session" on a conversation row: that chat's next session starts
    // fresh (v2: `agent_conversations.resetAt`).
    freshMyBridgeAgentConversation: builder.mutation<unknown, { agentId: string; chatId: string; bridgeId?: string }>({
      query: ({ agentId, chatId, bridgeId }) => ({ url: myBridgeAgentUrl(agentId, 'fresh-conversation', { bridgeId }), method: 'POST', body: { chatId } }),
      invalidatesTags: ['MyBridgeSessions'],
    }),
    // "Re-queue from here": the chat's customer messages held for credits / budget, from `entryId`
    // (a conversation bubble's id) on, go to the agent now. 409 = takeover on, 402 = still no credits.
    requeueMyBridgeAgentFromHere: builder.mutation<{ requeued: number }, { agentId: string; chatId: string; entryId: string; bridgeId?: string }>({
      query: ({ agentId, chatId, entryId, bridgeId }) => ({ url: myBridgeAgentUrl(agentId, 'requeue-from-here', { bridgeId }), method: 'POST', body: { chatId, entryId } }),
      invalidatesTags: ['MyBridgeSessions'],
    }),
    // "Reset all sessions": the same fresh start for every chat of the agent.
    resetMyBridgeAgentSessions: builder.mutation<unknown, { agentId: string; bridgeId?: string }>({
      query: ({ agentId, bridgeId }) => ({ url: myBridgeAgentUrl(agentId, 'reset-sessions', { bridgeId }), method: 'POST', body: {} }),
      invalidatesTags: ['MyBridgeSessions'],
    }),
    // ── the agent's public Tik profile ─────────────────────────────────────────
    getMyBridgeAgentTik: builder.query<AgentTik, { agentId: string }>({
      query: ({ agentId }) => ({ url: myBridgeAgentUrl(agentId, 'tik'), method: 'GET' }),
      providesTags: ['MyBridge'],
    }),
    saveMyBridgeAgentTik: builder.mutation<AgentTik, { agentId: string; content: TikContent; published?: boolean; phoneConsent?: boolean }>({
      query: ({ agentId, ...body }) => ({ url: myBridgeAgentUrl(agentId, 'tik'), method: 'POST', body }),
      invalidatesTags: ['MyBridge'],
    }),
    draftMyBridgeAgentTik: builder.mutation<TikContent, { agentId: string }>({
      query: ({ agentId }) => ({ url: myBridgeAgentUrl(agentId, 'tik/draft'), method: 'POST', body: {} }),
    }),
    deleteMyBridgeAgentTik: builder.mutation<{ ok: true }, { agentId: string }>({
      query: ({ agentId }) => ({ url: myBridgeAgentUrl(agentId, 'tik'), method: 'DELETE' }),
      invalidatesTags: ['MyBridge'],
    }),
    // ── the agent's Google Calendar (service-account key, write-only) ──────────
    getMyBridgeAgentCalendar: builder.query<MyBridgeAgentCalendar, { agentId: string }>({
      query: ({ agentId }) => ({ url: myBridgeAgentUrl(agentId, 'calendar'), method: 'GET' }),
      providesTags: ['MyBridge'],
    }),
    setMyBridgeAgentCalendar: builder.mutation<MyBridgeAgentCalendar, { agentId: string; calendarId: string; timeZone: string; serviceAccountJson?: string }>({
      query: ({ agentId, ...body }) => ({ url: myBridgeAgentUrl(agentId, 'calendar'), method: 'PUT', body }),
      invalidatesTags: ['MyBridge'],
    }),
    deleteMyBridgeAgentCalendar: builder.mutation<MyBridgeAgentCalendar, { agentId: string }>({
      query: ({ agentId }) => ({ url: myBridgeAgentUrl(agentId, 'calendar'), method: 'DELETE' }),
      invalidatesTags: ['MyBridge'],
    }),
    testMyBridgeAgentCalendar: builder.mutation<MyBridgeAgentCalendarTest, { agentId: string }>({
      query: ({ agentId }) => ({ url: myBridgeAgentUrl(agentId, 'calendar/test'), method: 'POST', body: {} }),
    }),
    // ── the agent's external MCP servers (credential write-only) ───────────────
    getMcpPresets: builder.query<McpPreset[], McpPresetQuery>({
      query: ({ q, category, limit }) => ({ url: withQuery('my-bridge/mcp-presets', { q, category, limit }), method: 'GET' }),
    }),
    /** The library's categories with counts (the directory's taxonomy). */
    getMcpCategories: builder.query<Array<{ id: string; count: number }>, void>({
      query: () => ({ url: 'my-bridge/mcp-categories', method: 'GET' }),
    }),
    getMyBridgeAgentMcpServers: builder.query<MyBridgeMcpServer[], { agentId: string }>({
      query: ({ agentId }) => ({ url: myBridgeAgentUrl(agentId, 'mcp-servers'), method: 'GET' }),
      providesTags: ['MyBridgeMcpServers'],
    }),
    connectMyBridgeAgentMcpServer: builder.mutation<ConnectMcpServerResult, ConnectMcpServerParams>({
      query: ({ agentId, ...body }) => ({ url: myBridgeAgentUrl(agentId, 'mcp-servers'), method: 'POST', body }),
      invalidatesTags: ['MyBridgeMcpServers'],
    }),
    patchMyBridgeAgentMcpServer: builder.mutation<MyBridgeMcpServer | ConnectMcpServerResult, PatchMcpServerParams>({
      query: ({ agentId, id, ...body }) => ({ url: myBridgeAgentUrl(agentId, `mcp-servers/${encodeURIComponent(id)}`), method: 'PATCH', body }),
      invalidatesTags: ['MyBridgeMcpServers'],
    }),
    removeMyBridgeAgentMcpServer: builder.mutation<{ id: string }, { agentId: string; id: string }>({
      query: ({ agentId, id }) => ({ url: myBridgeAgentUrl(agentId, `mcp-servers/${encodeURIComponent(id)}`), method: 'DELETE' }),
      invalidatesTags: ['MyBridgeMcpServers'],
    }),
    // ── knowledge base files (the agent's whole-file KB) ───────────────────────
    getMyBridgeAgentKnowledgeFiles: builder.query<{ files: MyBridgeKnowledgeFile[] }, { agentId: string }>({
      query: ({ agentId }) => ({ url: myBridgeAgentUrl(agentId, 'knowledge/files'), method: 'GET' }),
      providesTags: ['MyBridgeKnowledge'],
    }),
    getMyBridgeAgentKnowledgeFileContent: builder.query<{ name: string; content: string }, { agentId: string; name: string }>({
      query: ({ agentId, name }) => ({ url: myBridgeAgentUrl(agentId, `knowledge/files/${encodeURIComponent(name)}/content`), method: 'GET' }),
    }),
    uploadMyBridgeAgentKnowledgeFile: builder.mutation<{ files: MyBridgeKnowledgeFile[] }, { agentId: string; name: string; contentBase64: string }>({
      query: ({ agentId, name, contentBase64 }) => ({ url: myBridgeAgentUrl(agentId, 'knowledge/files'), method: 'POST', body: { name, contentBase64 } }),
      invalidatesTags: ['MyBridgeKnowledge'],
    }),
    deleteMyBridgeAgentKnowledgeFile: builder.mutation<{ files: MyBridgeKnowledgeFile[] }, { agentId: string; name: string }>({
      query: ({ agentId, name }) => ({ url: myBridgeAgentUrl(agentId, `knowledge/files/${encodeURIComponent(name)}`), method: 'DELETE' }),
      invalidatesTags: ['MyBridgeKnowledge'],
    }),
    getMyBridgeAgentConversation: builder.query<MyBridgeAgentConversation, { agentId: string; chatId: string; bridgeId?: string; limit?: number; skip?: number }>({
      query: ({ agentId, ...params }) => ({ url: myBridgeAgentUrl(agentId, 'conversation', params), method: 'GET' }),
    }),
    /** One chat session's Claude transcript (v2: the chat's session, by `chatId`). */
    getMyBridgeAgentTranscript: builder.query<MyBridgeAgentTranscript, { agentId: string; chatId: string; bridgeId?: string; limit?: number; includeThinking?: boolean }>({
      query: ({ agentId, chatId, bridgeId, limit, includeThinking }) => ({
        url: myBridgeAgentUrl(agentId, 'transcript', { chatId, bridgeId, limit, includeThinking: includeThinking ? '1' : undefined }),
        method: 'GET',
      }),
    }),
    getMyBridgeAgentActivity: builder.query<MyBridgeAgentActivity, { agentId: string; bridgeId?: string; chatId?: string; level?: string; limit?: number }>({
      query: ({ agentId, ...params }) => ({ url: myBridgeAgentUrl(agentId, 'activity', params), method: 'GET' }),
    }),

    getArchivedOrDeletedData: builder.mutation<any, { mode: string, itemId: string, itemUpdatedAt: string, searchQuery: string, type?: string[], date?: string, board?: string[] }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "get-archived-or-deleted-data",
          ...params,
        },
      }),
    }),
    restoreItems: builder.mutation<any, { items: ArchivedOrDeletedItem[], deleteOrArchive: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "restore-items",
          ...params,
        },
      }),
    }),
    deleteItems: builder.mutation<any, { items: ArchivedOrDeletedItem[] }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "delete-items",
          ...params,
        },
      }),
    }),
    getMyWork: builder.mutation<any, {}>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "get-my-work",
          ...params,
        },
      }),
    }),
    confirmEmail: builder.mutation<any, {}>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "send-email-confirmation",
          ...params,
        },
      }),
    }),
    startOrExtendTrial: builder.mutation<any, {}>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "start-or-extend-trial",
          ...params,
        },
      }),
    }),

    triggerBulkMigrationAction: builder.mutation<any, {}>({
      query: () => ({
        url: "actions?cmd=trigger-bulk-migration",
        method: "POST",
      }),
    }),
    triggerBackendChatbotEvent: builder.mutation<any, { data: any }>({
      query: (params) => ({
        url: "actions?cmd=handle-chatbot-event",
        method: "POST",
        body: {
          ...params,
          data: {
            ...params.data,
            triggerSource: 'blueticks extension'
          }
        }
      }),
    }),
    updateChargeDate: builder.mutation<
      Subscription,
      { subscriptionId: string; nextChargeDate: string }
    >({
      query: (params) => ({
        url: "backoffice-actions",
        method: "POST",
        body: {
          cmd: "updateChargeDate",
          subscriptionId: params.subscriptionId,
          nextChargeDate: params.nextChargeDate,
        },
      }),
    }),
    getItemPrices: builder.mutation<GetItemPricesResponse, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.getItemPrices" },
      })
    }),
    getWaMessagesAckDataByWaMessagesKeys: builder.mutation<any, { messageKeys: Array<{ key: string, key2: string }> }>({
      query: (params) => ({
        url: "/actions",
        method: "POST",
        body: {
          cmd: "whatsapp-interface.getWaMessagesAckDataByWaMessagesKeys",
          params,
        },
      }),
    }),
    getMessageInfo: builder.mutation<any, { waMessageKey: string }>({
      query: (params) => ({
        url: "/actions",
        method: "POST",
        body: {
          cmd: "whatsapp-interface.getMessageInfo",
          params,
        },
      }),
    }),
    // #1024 - does this account have a Gateway, and how many sends are waiting on it? The plan page
    // warns before a purchase that drops the Gateway. Read-only.
    getGatewayImpact: builder.mutation<GatewayImpact, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.getGatewayImpact" },
      }),
    }),
    getEntitlements: builder.mutation<Record<string, Entitlement[]>, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.getEntitlements" },
      }),
    }),
    // Owned subscription item_price_ids (plan + add-ons) — the Add-Credits modal
    // disables a credit pack the subscriber already owns (serenix-com/Blueticks#143;
    // on_off packs can't be bought twice). Read-only.
    getOwnedSubscriptionItemIds: builder.mutation<{ itemPriceIds: string[] }, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.getOwnedSubscriptionItemIds" },
      }),
    }),
    associateSubscription: builder.mutation<void, { customerId: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.associateSubscription", ...params },
      }),
    }),
    queryKnowledgeBase: builder.mutation<QueryKnowledgeBaseResponse, QueryKnowledgeBaseParams>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "knowledge-base.query",
          ...params
        },
      }),
    }),
    getFacebookPages: builder.mutation<GetPagesResponse, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "facebook.getPages",
        },
      }),
    }),
    getCreditUsage: builder.mutation<CreditUsageResponse, { timeframe: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "credits.getCreditUsage",
          ...params
        },
      }),
    }),
    // #357 auto top-up settings (workspace owner only; the workspace comes from the session).
    getAutoTopUp: builder.mutation<AutoTopUpView, void>({
      query: () => ({ url: "actions", method: "POST", body: { cmd: "credits.getAutoTopUp" } }),
    }),
    setAutoTopUp: builder.mutation<AutoTopUpView, AutoTopUpSetRequest>({
      query: (request) => ({ url: "actions", method: "POST", body: { cmd: "credits.setAutoTopUp", ...request } }),
    }),
    calculateBotStats: builder.mutation<any, string[]>({
      query: (botIds) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "calculate-bot-stats",
          botIds
        },
      }),
      async onQueryStarted(botIds, { dispatch, queryFulfilled }) {
        try {
          const { data: botStats } = await queryFulfilled;

          if (botStats && Array.isArray(botStats)) {
            botStats.forEach(botWithStats => {
              if (botWithStats._id && botWithStats.botStats) {
                dispatch({
                  type: `bot/updateOne`,
                  payload: {
                    id: botWithStats._id,
                    changes: { botStats: botWithStats.botStats }
                  }
                });
              }
            });
          }
        } catch (error) {
          console.error('Error updating bot stats:', error);
        }
      }
    }),
    queryAndAnswer: builder.mutation<
      { generatedAnswer: string; rerankedResponse: any },
      { knowledgeBaseIds: string[]; documentIds?: string[]; query: string; limit?: number; similarityThreshold?: number }
    >({
      query: (body) => ({
        url: '/actions',
        method: 'POST',
        body: {
          cmd: 'knowledge-base.queryAndAnswer',
          ...body,
        },
      }),
    }),
    subscribeToPageMessaging: builder.mutation<SubscribeToPageMessagingResponse, SubscribeToPageMessagingRequest>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "instagram.subscribeToPageMessaging", ...params },
      }),
    }),
    unsubscribeFromPageMessaging: builder.mutation<UnsubscribeFromPageMessagingResponse, UnsubscribeFromPageMessagingRequest>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "instagram.unsubscribeFromPageMessaging", ...params },
      }),
    }),
    // Get auth tokens for the current user
    getAuthTokens: builder.query<any[], void>({
      query: () => ({
        url: "auth-tokens",
        method: "GET"
      }),
      transformResponse: (response: any) => {
        // Handle paginated response
        if (response && response.data) {
          return response.data;
        }
        return response;
      }
    }),

    // Delete an auth token
    deleteAuthToken: builder.mutation<void, string>({
      query: (tokenId) => ({
        url: `auth-tokens/${tokenId}`,
        method: "DELETE"
      }),
      // Invalidate the auth tokens query when a token is deleted
      invalidatesTags: [{ type: 'AuthTokens', id: 'LIST' }]
    }),
    revokeToken: builder.mutation<RevokeTokenResponse, RevokeTokenRequest>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "instagram.revokeToken", ...params },
      }),
      invalidatesTags: ["Instagram"],
    }),
    refreshToken: builder.mutation<RefreshTokenResponse, RefreshTokenRequest>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "instagram.refreshToken", ...params },
      }),
      invalidatesTags: [{ type: 'AuthTokens', id: 'LIST' }],
    }),
    // Validate Facebook token
    validateFacebookToken: builder.mutation<ValidateFacebookTokenResponse, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "facebook.validateToken" },
      }),
    }),
    // Dashboard statistics endpoints
    getDashboardOverviewStats: builder.mutation<DashboardOverviewStats, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "dashboard.getOverviewStats" },
      }),
    }),

    getNodeTypeDistribution: builder.mutation<NodeTypeDistributionItem[], void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "dashboard.getNodeTypeDistribution" },
      }),
    }),

    getAdvancedFeatureAdoption: builder.mutation<FeatureAdoptionItem[], void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "dashboard.getAdvancedFeatureAdoption" },
      }),
    }),

    getKnowledgeBaseAdoption: builder.mutation<KnowledgeBaseAdoptionStats, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "dashboard.getKnowledgeBaseAdoption" },
      }),
    }),

    getAllDashboardStats: builder.mutation<AllDashboardStats, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "dashboard.getAllStats" },
      }),
    }),

    getBotList: builder.mutation<BotListItem[], void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "dashboard.getBotList" },
      }),
    }),
    sendInstagramMessage: builder.mutation<SendInstagramMessageResponse, SendInstagramMessageRequest>({
      query: (params) => ({
        url: 'actions',
        method: 'POST',
        body: {
          cmd: 'instagram.sendMessage',
          data: params
        }
      }),
      invalidatesTags: []
    }),
    findSessionsByNodeEvent: builder.query<
      Paginated<AutomationSession>,
      { eventType?: EventType; processType?: string; limit?: number; skip?: number }
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "session.findSessionsByNodeEvent",
          ...params
        },
      }),
    }),
    getAllSessions: builder.query<
      Paginated<any>,
      GetAllSessionsParams
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "dashboard.getAllSessions",
          ...params
        },
      }),
    }),
    /** #1155 S-MN1 step 1: the account's Numbers + which add-gateway targets the create gate would accept.
     * Provides 'Session' so creating or removing a gateway refetches it. */
    getNumbers: builder.query<NumbersView, void>({
      query: () => ({ url: "numbers", method: "GET" }),
      providesTags: ['Session'],
    }),
    // Session CRUD operations
    createSession: builder.mutation<any, any>({
      query: (sessionData) => ({
        url: "sessions",
        method: "POST",
        body: sessionData,
      }),
      invalidatesTags: ['Session']
    }),
    updateSession: builder.mutation<any, { id: string;[key: string]: any }>({
      query: ({ id, ...patch }) => ({
        url: `sessions/${id}`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: ['Session']
    }),
    removeSession: builder.mutation<any, string>({
      query: (id) => ({
        url: `sessions/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ['Session']
    }),
    // K8s Engine Management
    createK8sEngine: builder.mutation<any, { sessionId: string; engineConfig?: any }>({
      query: ({ sessionId, engineConfig }) => ({
        url: "k8s-engine-manager",
        method: "POST",
        body: {
          sessionId,
          action: 'create',
          ...(engineConfig && { engineConfig })
        },
      }),
      invalidatesTags: ['Session']
    }),
    // #844: owner-scoped orphan probe, used to confirm a failed engine create left no deployment.
    checkK8sEngineResources: builder.mutation<unknown, void>({
      query: () => ({
        url: "k8s-engine-manager",
        method: "POST",
        body: { action: 'check-resources' },
      }),
    }),
    deleteK8sEngine: builder.mutation<any, string>({
      // Companion to backend #504 (audit E): "Remove gateway" must explicitly opt into deleting
      // the PVC (the WhatsApp auth store) — the backend reads `?deletePvc=true` and deletes the
      // PVC only for this flagged, owner-JWT, doc-present DELETE; a bare DELETE keeps it.
      query: (sessionId) => ({
        url: deleteK8sEngineUrl(sessionId),
        method: "DELETE",
      }),
      invalidatesTags: ['Session']
    }),
    restartK8sEngine: builder.mutation<any, string>({
      query: (sessionId) => ({
        url: "k8s-engine-manager",
        method: "POST",
        body: {
          sessionId,
          action: 'restart'
        },
      }),
      invalidatesTags: ['Session']
    }),
    getK8sEngineStatus: builder.query<any, string>({
      query: (sessionId) => ({
        url: `k8s-engine-manager/${sessionId}`,
        method: "GET",
      }),
    }),
    listK8sEngines: builder.query<any[], void>({
      query: () => ({
        url: "k8s-engine-manager",
        method: "GET",
      }),
    }),
    getConnectedClients: builder.mutation<ConnectedSocket[], void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "emqx.getConnectedClients" },
      }),
    }),
    generateSupportedUserToken: builder.mutation<GenerateSupportedUserTokenResponse, GenerateSupportedUserTokenParams>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "dashboard.generateSupportedUserToken",
          supportedUserId: params.supportedUserId
        },
      }),
    }),
    getUserConnectedClients: builder.mutation<ConnectedSocket[], GetUserConnectedClientsParams>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "emqx.getUserConnectedClients",
          userId: params.userId
        },
      }),
    }),
    refundClient: builder.mutation<any, {}>({
      query: (params) => ({
        url: "backoffice-actions",
        method: "POST",
        body: {
          cmd: "refund-client",
          ...params,
        },
      }),
    }),
    syncUserData: builder.mutation<any, {}>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "sync-user-data",
          ...params,
        },
      }),
    }),
    deleteUserAccount: builder.mutation<any, { userId: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "delete-user-account",
          ...params,
        },
      }),
    }),
    getEngineStatus: builder.mutation<any, { engineId: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "engine:get-status",
          ...params,
        },
      }),
    }),
    pingEngine: builder.mutation<any, { engineId: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "engine:ping",
          ...params,
        },
      }),
    }),
    getEngineProfilePics: builder.mutation<any, { engineId: string; contactIds: string[] }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "engine:get-profile-pic",
          ...params,
        },
      }),
    }),
    assignWorkspaceUserInviteId: builder.mutation<
      { success: boolean; workspaceUser: any; message: string; error?: string },
      { workspaceId: string; email: string }
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "assign-workspace-user-invite-id",
          ...params,
        },
      }),
    }),
    subscribeToFCMTopic: builder.mutation<
      { success: boolean; message: string; topicName: string; userId: string; error?: string },
      { fcmToken: string }
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "subscribe-to-fcm-topic",
          ...params,
        },
      }),
    }),
    retryMessage: builder.mutation<
      { success: boolean; messageId: string; error?: string },
      { messageId: string }
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "retry-message",
          ...params,
        },
      }),
    }),
    retryMessagesBatch: builder.mutation<
      { success: boolean; results: Array<{ messageId: string; success: boolean }>; errors: Array<{ messageId: string; error: string }>; total: number; successful: number; failed: number; error?: string },
      { messageIds: string[] }
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "retry-messages-batch",
          ...params,
        },
      }),
    }),
    recordDunningEvent: builder.mutation<any, { name: string; props?: Record<string, any> }>({
      query: ({ name, props }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "record-dunning-event", name, props: props ?? {} },
      }),
    }),
    getNudge: builder.query<GetNudgeResponse, GetNudgeRequest | void>({
      query: (req) => ({
        url: "actions",
        method: "POST",
        // #787: this build renders review asks; older builds omit the flag and are never served one.
        body: { cmd: "get-nudge", ...(req ?? {}), supportsReviewAsk: true } satisfies GetNudgeRequest & { cmd: string },
      }),
      // Keep the cached payload alive across rapid extension re-injection gaps
      // (WhatsApp Web DOM churn tears down + remounts the React root constantly).
      keepUnusedDataFor: 300,
    }),
    /** #693 — live Pro-trial state, days left computed on the server clock. */
    getProTrialStatus: builder.query<ProTrialStatus, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "pro-trial-status" },
      }),
      keepUnusedDataFor: 300,
    }),
    /** #131 T2 — which free-tier limit arm this user is in (server-assigned; the client
     * cannot compute it: FE holdout hashing is djb2, BE assignment is sha256).
     * The gate FAILS CLOSED to FREE_BASELINE_POLICY whenever this has not resolved. */
    getFreeLimit: builder.query<FreeLimitPolicy, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "get-free-limit" },
      }),
      // Same rationale as getNudge: survive WhatsApp-Web re-injection remounts.
      keepUnusedDataFor: 300,
      // Refresh is owned by free-limit-slice (it re-fetches with forceRefetch after every
      // schedule-create), NOT by tag invalidation — the slice is the single source of truth
      // for the gate, so keeping a second cache-invalidation path would just be two ways to
      // do the same thing.
    }),
    /** #113 FE-1 — the first-week guided checklist (steps/workflow/ownWid).
     * Same keepUnusedDataFor rationale as getNudge/getFreeLimit above. */
    getFirstWeekChecklist: builder.query<FirstWeekChecklist, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "get-first-week-checklist" },
      }),
      keepUnusedDataFor: 300,
    }),
    recordFirstWeekEvent: builder.mutation<
      { success: boolean } | Record<string, unknown>,
      {
        name: "first_week_checklist_shown" | "first_week_checklist_step_clicked";
        props: { step: StepId; surface: "web" | "extension" };
      }
    >({
      query: ({ name, props }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "record-first-week-event", name, props },
      }),
    }),
    /** #603 agent-day-1 A/B prompt. `state` is the only visibility source (server checks the
     * kill switch + arm + day window); fetched once per app load so a recorded show is not
     * immediately re-hidden by a refetch. */
    getAgentDay1PromptState: builder.query<AgentDay1PromptState, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "agent-day1-prompt", op: "state", surface: "web" },
      }),
      keepUnusedDataFor: 3600,
    }),
    recordAgentDay1Prompt: builder.mutation<{ success: boolean; recorded?: boolean } | { error: string }, AgentDay1PromptOp>({
      query: (op) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "agent-day1-prompt", op, surface: "web" },
      }),
    }),
    // #357 credits-page funnel. Fire-and-forget; server allowlists name + prop keys.
    recordCreditsEvent: builder.mutation<
      { success: boolean } | Record<string, unknown>,
      {
        name: "credits_page_view" | "add_credits_open" | "pack_select" | "addon_select" | "checkout_start";
        props: Record<string, string | number | boolean>;
      }
    >({
      query: ({ name, props }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "record-credits-event", name, props },
      }),
    }),
    recordNudgeEvent: builder.mutation<
      { success: boolean } | Record<string, unknown>,
      { name: NudgeClientEventName; props: NudgeEventProps }
    >({
      query: ({ name, props }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "record-nudge-event", name, props: props ?? {} },
      }),
    }),
    // MCP connector client funnel (dialog). The backend action derives the user
    // from the JWT, so the client only ever sends {name, props}. Mirrors
    // recordNudgeEvent above — server-owned events (mcp_oauth_approved,
    // mcp_tool_used) are NOT reportable here (allowlist-gated backend-side).
    recordMcpEvent: builder.mutation<
      { success: boolean } | Record<string, unknown>,
      { name: McpClientEventName; props?: Record<string, unknown> }
    >({
      query: ({ name, props }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "record-mcp-event", name, props: props ?? {} },
      }),
    }),
    // #1165 - which pricing arm this account is in. Read-only; the backend ships it paused,
    // so everyone is 'control' until the experiment is switched on.
    resolveMcpPricingArm: builder.mutation<{ arm?: string } | Record<string, unknown>, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "mcp.resolvePricingArm" },
      }),
    }),
    // Connect-WhatsApp first-run surface funnel (activation instrumentation). The backend
    // derives the user from the JWT; the client sends {name, props, source?}. `source`
    // opts into 'extension' for the connect chip when it renders inside the browser
    // extension list view (the banner + hero are dashboard-only → default 'web').
    recordConnectEvent: builder.mutation<
      { success: boolean } | Record<string, unknown>,
      { name: ConnectClientEventName; props?: Record<string, unknown>; source?: "web" | "extension" }
    >({
      query: ({ name, props, source }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "record-connect-event", name, props: props ?? {}, ...(source ? { source } : {}) },
      }),
    }),
    // #651 — throttled "dead click" diagnostics (ui_action_stalled). Fire-and-forget from
    // the ui-stall-reporter sink; props are identifiers/numbers only, never content.
    recordUiDiagnosticEvent: builder.mutation<
      { success: boolean } | Record<string, unknown>,
      { name: UiDiagnosticEventName; props: Record<string, unknown> }
    >({
      query: ({ name, props }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "record-ui-diagnostic-event", name, props },
      }),
    }),
    // Capture the browser's IANA timezone onto the current workspace, SET-IF-EMPTY
    // only (ships with/before the BE#648 DST release). The backend derives the
    // workspace from the JWT (user.currentWorkspace), validates the zone against
    // the canonical IANA set, and writes with a DB-level conditional so a value
    // that is already set is never overwritten. The client only sends {timezone};
    // the decision of WHETHER to call lives in the shared post-auth bootstrap via
    // the pure `shouldCaptureTimezone` helper.
    captureWorkspaceTimezone: builder.mutation<
      { success: boolean; captured?: boolean; timezone?: string } | Record<string, unknown>,
      { timezone: string }
    >({
      query: ({ timezone }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "capture-workspace-timezone", timezone },
      }),
    }),
    // BE#710 — failure breakdown for a campaign, computed server-side over the WHOLE campaign
    // (the recipient list is lazy-loaded, so a client count would undercount). Owner-checked
    // via the JWT. Drives the "numbers aren't on WhatsApp" banner.
    campaignFailureBreakdown: builder.query<CampaignFailureBreakdown, { campaignId: string }>({
      query: ({ campaignId }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "campaign-failure-breakdown", campaignId },
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetMediaTripStateMutation,
  useRequestMediaReloadMutation,
  useGetSignedUploadUrlMutation,
  useAuthenticateMutation,
  useRegisterMutation,
  useGenerateUploadUrlMutation,
  useGetEntityQuery,
  usePatchEntityMutation,
  useSendResetPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  usePasskeyStatusMutation,
  usePasskeySignInStatusMutation,
  usePasskeySignInOptionsMutation,
  usePasskeyListMutation,
  usePasskeyRegisterOptionsMutation,
  usePasskeyRegisterVerifyMutation,
  usePasskeyRenameMutation,
  usePasskeyRemoveMutation,
  useCancelSubscriptionMutation,
  useAddUserToTaskGroupMutation,
  useAddNewBoardMemberApiMutation,
  useFindEntityQuery,
  useGetTotalCountQuery,
  useUnsubscribeFromListmonkMutation,
  useCreateEntityMutation,
  useAddExistingWorkspaceUserToBoardMutation,
  useAddNonExistingWorkspaceUserToBoardMutation,
  useResendBoardInvitationMutation,
  useSendAddToWorkspaceMutation,
  useAcceptWorkspaceInventionMutation,
  useAcceptBoardInventionMutation,
  useGetLinkPreviewMutation,
  useDuplicateGroupMutation,
  useOrderDetailsMutation,
  useGetUserPaymentParamsMutation,
  useGetPlanMapMutation,
  useChangePlanMutation,
  useCompletePurchaseMutation,
  useCalculateCartMutation,
  useGetWorkspaceInvoicesMutation,
  useGetInvoiceMutation,
  useGetShopperInfoMutation,
  useGetPaypalPaymentUrlMutation,
  useUpdateShopperInfoMutation,
  useImportExcelToBoardMutation,
  useInitialStepMutation,
  useGetNextStepMutation,
  useGetMyWorkMutation,
  useConfirmEmailMutation,
  useStartOrExtendTrialMutation,
  useGenerateApiKeyMutation,
  useListApiKeysQuery,
  useListPausedSeriesQuery,
  useResumePausedSeriesMutation,
  useCreateApiKeyMutation,
  useRenameApiKeyMutation,
  useRevokeApiKeyMutation,
  useCreateAndDeleteAutomationMutation,
  useTriggerBulkMigrationActionMutation,
  useGetEmailWorkspacesMutation,
  useUpdateSubscriptionWorkspaceMutation,
  useGetCheckoutPageUrlMutation,
  useGetItemPricesMutation,
  useGetScheduledMessagesInRedisQuery,
  useGetEntitlementsMutation,
  useGetGatewayImpactMutation,
  useGetOwnedSubscriptionItemIdsMutation,
  useProcessDocumentMutation,
  useGenerateAIResponseMutation,
  useChatbotTestInboundMutation,
  useQueryKnowledgeBaseMutation,
  useQueryAndAnswerMutation,
  useGetFacebookPagesMutation,
  useGetWaMessagesAckDataByWaMessagesKeysMutation,
  useGetMessageInfoMutation,
  useGetCreditUsageMutation,
  useGetAutoTopUpMutation,
  useSetAutoTopUpMutation,
  useCalculateBotStatsMutation,
  useSendEmailFromClientMutation,
  useSubscribeToPageMessagingMutation,
  useUnsubscribeFromPageMessagingMutation,
  useGetAuthTokensQuery,
  useDeleteAuthTokenMutation,
  useRevokeTokenMutation,
  useRefreshTokenMutation,
  useValidateFacebookTokenMutation,
  useGetDashboardOverviewStatsMutation,
  useGetNodeTypeDistributionMutation,
  useGetAdvancedFeatureAdoptionMutation,
  useGetKnowledgeBaseAdoptionMutation,
  useGetAllDashboardStatsMutation,
  useGetBotListMutation,
  useSendInstagramMessageMutation,
  useFindSessionsByNodeEventQuery,
  useGetAllSessionsQuery,
  useGetConnectedClientsMutation,
  useGenerateSupportedUserTokenMutation,
  useGetUserConnectedClientsMutation,
  useDeleteUserAccountMutation,
  // Engine Commands

  useGetEngineStatusMutation,
  usePingEngineMutation,
  useAssignWorkspaceUserInviteIdMutation,
  useSubscribeToFCMTopicMutation,
  useRetryMessageMutation,
  useRetryMessagesBatchMutation,
  useBulkDeleteEntitiesMutation,
  useLaunchDripMutation,
  usePauseDripMutation,
  useResumeDripMutation,
  useAbortDripMutation,
  useSkipNextOccurrenceMutation,
  useSkipDripOccurrenceMutation,
  useSkipDripStepMutation,
  useUnskipDripStepMutation,
  useBeginCampaignEditMutation,
  useCommitCampaignEditMutation,
  useAbortCampaignEditMutation,
  useEditPendingCampaignMutation,
  useRecordDunningEventMutation,
  useGetNudgeQuery,
  useGetProTrialStatusQuery,
  useGetFreeLimitQuery,
  useRecordNudgeEventMutation,
  useRecordMcpEventMutation,
  useResolveMcpPricingArmMutation,
  useRecordConnectEventMutation,
  useCaptureWorkspaceTimezoneMutation,
  useCampaignFailureBreakdownQuery,
  useGetFirstWeekChecklistQuery,
  useRecordFirstWeekEventMutation,
  useGetAgentDay1PromptStateQuery,
  useRecordAgentDay1PromptMutation,
  useRecordCreditsEventMutation,
  useGetCancelFlowConfigQuery,
  useGetWithdrawalContextQuery,
  useSubmitWithdrawalMutation,
} = backendApi;
export const backendApiUseMutations = backendApi;

export const {
  addExistingWorkspaceUserToBoard,
  addNonExistingWorkspaceUserToBoard,
  resendBoardInvitation,
  authenticate,
  getEntity,
  findEntity,
  getTotalCount,
  unsubscribeFromListmonk,
  createEntity,
  createManyEntities,
  patchEntity,
  deleteEntity,
  deleteCampaign,
  resumeCampaign,
  nextCampaignMessage,
  getInProgressCampaigns,
  getScheduledMessagesInRedis,
  duplicateAudienceBoardForCampaigns,
  duplicateTemplateForCampaigns,
  removeAllPendingUserMessagesByCampaignId,
  updateManyEntities,
  changePlan,
  cancelSubscription,
  updateChargeDate,
  getSettings,
  createAndDeleteAutomation,
  checkSession,
  createUserSession,
  getArchivedOrDeletedData,
  restoreItems,
  deleteItems,
  getMyWork,
  triggerBulkMigrationAction,
  waMessageListenerEvent,
  syncUserData,
  refundClient,
  canISend: wasSendAttempted,
  updateShopperInfo,
  getShopperInfo,
  getCheckoutPageUrl,
  triggerBackendChatbotEvent,
  processDocument,
  queryKnowledgeBase,
  queryAndAnswer,
  getFacebookPages,
  getWaMessagesAckDataByWaMessagesKeys,
  getMessageInfo,
  getCreditUsage,
  calculateBotStats,
  sendEmailFromClient,
  subscribeToPageMessaging,
  unsubscribeFromPageMessaging,
  revokeToken,
  refreshToken,
  validateFacebookToken,
  getDashboardOverviewStats,
  getNodeTypeDistribution,
  getAdvancedFeatureAdoption,
  getKnowledgeBaseAdoption,
  getAllDashboardStats,
  getBotList,
  sendInstagramMessage,
  findSessionsByNodeEvent,
  getAllSessions,
  createSession,
  updateSession,
  removeSession,
  createK8sEngine,
  deleteK8sEngine,
  restartK8sEngine,
  getK8sEngineStatus,
  listK8sEngines,
  getConnectedClients,
  generateSupportedUserToken,
  getUserConnectedClients,
  deleteUserAccount,
  // Engine Commands
  getEngineStatus,
  pingEngine,
  getEngineProfilePics,
  assignWorkspaceUserInviteId,
  retryMessage,
  retryMessagesBatch,
  recordDunningEvent,
  getNudge,
  recordNudgeEvent,
  recordMcpEvent,
  resolveMcpPricingArm,
} = backendApi.endpoints;
export const backendApiEndpoints = backendApi.endpoints;
export const middleware = backendApi.middleware;
export default backendApi.reducer;

/**
 * Re-read ONLY `User.claudeAgentsActive` (server-computed) into the logged-in user. The WA tab
 * forwards incoming messages to the agents backend only when it is true, and it is otherwise
 * read once at login — so an agent turned on after WhatsApp loaded got no messages until a reload.
 */
export const refreshClaudeAgentsActiveThunk = createAsyncThunk<boolean | null, void>(
  'refreshClaudeAgentsActiveThunk',
  async (_, { dispatch, getState }) => {
    const user = (getState() as { UserReducer?: { user?: UserInterface | null } }).UserReducer?.user;
    const id = user?._id ? String(user._id) : '';
    if (!id) return null;
    const sub = dispatch(backendApi.endpoints.getUserById.initiate(id, { forceRefetch: true }));
    const res = await sub;
    sub.unsubscribe();
    const fresh = (res.data as { claudeAgentsActive?: boolean } | undefined)?.claudeAgentsActive === true;
    const current = (getState() as { UserReducer?: { user?: UserInterface | null } }).UserReducer?.user;
    if (current && (current as { claudeAgentsActive?: boolean }).claudeAgentsActive !== fresh) {
      const { setUser } = await import(/* webpackMode: "eager" */ "@common-reducers/UserReducer");
      dispatch(setUser({ ...current, claudeAgentsActive: fresh } as UserInterface));
    }
    return fresh;
  },
);
